import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Guid } from 'typescript-guid';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EventName, EventsService } from '../../../services/events/events.service';
import { NavigationService, PageName } from '../../../services/navigation/navigation.service';
import { NotificationService, ToastStyle } from '../../../services/notification/notification.service';
import { Contact, ContactAddress } from '../../../classes/contact/contact.class';
import { IONote } from '../../../classes/io/io-note.class';
import { IndFormFieldViewDataModel, FormFieldType } from '../../../models/indFormFieldDataModel';
import { Section } from '../../shared/ind-form-list/ind-form-list.component';
import {
  SdrFormState,
  SampleDeliveryRequestService,
  SdrNotesGate,
} from '../../../services/sample/sample-delivery-request.service';
import {
  SampleDeliveryRequest,
  SdrAllocationDisplay,
  SdrIoConfigLimit,
  SdrMeetingWindow,
  SdrPickerSku,
  SdrProductLine,
  SdrStatus,
  isValidEmail,
} from '../../../classes/sample/sample-delivery-request.class';
import {
  SampleDeliveryRequestDataService,
  StagedSdrNote,
} from '../../../data-services/sample/sample-delivery-request.data.service';
import { ContactOfflineService } from '../../../services/contact/contact.service';
import { ComponentViewMode } from '../../../services/ui/ui.service';
import { DeviceService } from '../../../services/device/device.service';
import { SdrNotesSectionComponent } from '../../sample/sdr-notes-section/sdr-notes-section.component';
import { ActivityService } from '../../../services/activity/activity.service';
import { AppointmentActivity } from '../../../classes/activity/appointment.activity.class';

interface SdrAddressOption {
  id: string;
  label: string;
  isPrimary: boolean;
}

interface SdrEmailOption {
  id: string;
  label: string;
  isPrimary: boolean;
}

// Numeric dropdown ceiling when a SKU has no configured maxQty — matches the
// Sample-Drop (IAO) implicit ceiling of 10 so the two in-meeting flows look alike.
const QTY_DROPDOWN_DEFAULT_MAX = 10;

// Quiet period before a qty change is actually sent. The stepper has no blur
// event to hang a save off (unlike Sample Drop's typed lot input, which saves
// on ind-form-field's 'input_value_confirm'), so without this every tap would
// enqueue its own PATCH — and since _enqueuePersist starts the first op
// immediately, a fast +/+/+ sends an INTERMEDIATE value before the trailing
// duplicates get skipped. Coalescing to the settled value means one call per
// adjustment, which matters on the low-end devices and poor connectivity reps
// actually work on.
const QTY_PERSIST_DEBOUNCE_MS = 600;

/**
 * Inline SDR editor embedded in the meeting detail pane's "Samples & Materials"
 * → Request tab (OMNI-61002). Mirrors the inline-allocation-order chrome and
 * placement, and reuses SampleDeliveryRequestService for form state and
 * validation. Like allocation, this now autosaves a real server-side Draft
 * record up front — the parent (activities-details-pane.ts's
 * createNewInlineSdr) creates that header record itself, mirroring
 * createNewInlineAllocation, so this component only ever mounts once a real
 * `sdr.id` already exists — and saves every product/note/contact edit on
 * click as it happens. Submit no longer creates anything; it just waits for
 * those saves to finish and finalizes the Draft to Submitted (`submit()` ->
 * `SampleDeliveryRequestDataService.finalizeSdr`). Offline-aware throughout —
 * every save-on-click call has an offline sibling, and finalizeSdr itself
 * flips a local pendingSync doc when offline so the row still appears
 * immediately.
 */
@Component({
  selector: 'inline-sample-delivery-request',
  templateUrl: 'inline-sample-delivery-request.html',
  styleUrls: ['inline-sample-delivery-request.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InlineSampleDeliveryRequestComponent implements OnInit, OnChanges, OnDestroy {
  /** null/new => editable draft; a saved SDR => read-only inline row. */
  @Input() sdr: SampleDeliveryRequest | null = null;
  @Input() isReadOnly = false;
  @Input() meetingId: string;
  @Input() meetingContacts: Contact[] = [];
  @Input() meetingStart: Date | null = null;
  @Input() meetingEnd: Date | null = null;

  /** OMNI-61513: allocation configs must span the meeting. App-side only —
   *  Dynamics checks the config against today and never against the meeting. */
  private get _meetingWindow(): SdrMeetingWindow | null {
    if (!this.meetingStart && !this.meetingEnd) return null;
    return { start: this.meetingStart, end: this.meetingEnd };
  }

  /** Emitted after a successful submit — parent refreshes the section list. */
  @Output() onSaved = new EventEmitter<SampleDeliveryRequest>();
  /** Emitted when a customer is chosen so the parent can add it to the meeting. */
  @Output() onContactSelected = new EventEmitter<{ contact: Contact }>();

  @ViewChild(SdrNotesSectionComponent) private notesSection?: SdrNotesSectionComponent;

  formState: SdrFormState;
  limitsMap: Map<string, SdrIoConfigLimit> = new Map();

  /** The contact backing the current draft — drives address/email options. */
  private selectedContact: Contact | null = null;

  /** ind-form-field viewData for the Customer LIST field — mirrors IAO's contact picker chrome. */
  contactFormField: IndFormFieldViewDataModel;
  private contactSourceData: any[] = [];

  /** Contacts surfaced by nearMePillsLoader below — tracked so
   *  sharedLocationPillsLoader can exclude anyone already shown there.
   *  Mirrors inline-allocation-order.ts's identical field/loader pair. */
  private nearMeContacts: Contact[] = [];
  nearMePillsLoader = async (): Promise<{ id: string; label: string; onClick: () => void }[]> => {
    const meeting = this.activityService.selectedActivity as AppointmentActivity;
    const excludeIds = new Set<string>();
    if (this.formState?.contactId) {
      excludeIds.add(this.formState.contactId);
    }
    this.nearMeContacts = await this.contactService.loadNearMeContacts(meeting, excludeIds);
    return this.nearMeContacts.map((c) => ({
      id: c.ID,
      label: this._contactDisplayName(c),
      // Must also emit onContactSelected (not just applyContact) — that's
      // the event activities-details-pane.ts's onSdrContactSelected listens
      // on to actually add this contact to the meeting. Without it,
      // applyContact alone updates the SDR's own contact fields fine, but
      // the contact silently never gets added as a meeting attendee.
      onClick: () => {
        this.applyContact(c);
        this.onContactSelected.emit({ contact: c });
      },
    }));
  };
  sharedLocationPillsLoader = async (): Promise<{ id: string; label: string; onClick: () => void }[]> => {
    const currentContactId = this.formState?.contactId;
    const meetingContactIds = (this.meetingContacts || []).map((c) => c.ID);
    const sourceContactIds = currentContactId ? [currentContactId] : meetingContactIds;
    if (!sourceContactIds.length) return [];
    const excludeIds = new Set([...this.nearMeContacts.map((c) => c.ID), ...(currentContactId ? [currentContactId] : [])]);
    const currentAddress: ContactAddress | undefined = this.selectedContact?.addressesList?.find(
      (a) => a.addressId === this.formState?.deliveryAddressId,
    );
    const contacts = await this.contactService.loadSharedLocationContacts(sourceContactIds, excludeIds, currentAddress);
    return contacts.map((c) => ({
      id: c.ID,
      label: this._contactDisplayName(c),
      // Same fix as nearMePillsLoader above — emit onContactSelected so the
      // contact actually gets added to the meeting, not just applied to
      // this SDR's own formState.
      onClick: () => {
        this.applyContact(c);
        this.onContactSelected.emit({ contact: c });
      },
    }));
  };

  addressOptions: SdrAddressOption[] = [];
  emailOptions: SdrEmailOption[] = [];
  isAddressDropdownOpen = false;
  isEmailDropdownOpen = false;

  isPickerOpen = false;
  pickerSearchText = '';
  isSubmitting = false;

  /**
   * This draft's own server-side record id. Set directly in _initResumeState
   * from the real `sdr.id` the parent hands in — createNewInlineSdr (parent)
   * now creates the header record itself before ever mounting this
   * component (mirrors createNewInlineAllocation), so `sdr` always carries a
   * real id by the time ngOnInit runs; there is no blank/create-on-mount
   * case left to handle here. Submit no longer creates anything atomically
   * (see submit()), so this id is not passed to sdrService.submit()/
   * submitOffline()/reconcileSubmit() — those remain used only by the
   * standalone new-sample-delivery-request form's own, deliberately-
   * unchanged atomic-create flow.
   */
  private draftSdrId: string | null = null;
  /** Server-side line id (online) per product line, keyed by OBJECT
   *  IDENTITY (the exact SdrProductLine instance pushed into
   *  formState.products), not by skuId — this correctly distinguishes a
   *  removed-then-re-added line sharing the same SKU from the new instance
   *  that replaced it (skuId alone can't tell them apart). Undefined/absent
   *  means either offline-added (no server id exists), the add is still in
   *  flight, or it was rejected. Read fresh inside each enqueued op (never
   *  captured at call time) so a same-line remove correctly observes
   *  whatever the add op eventually wrote — the FIFO persist queue
   *  guarantees the add's op fully completes before the remove's op runs.
   *  KNOWN LIMITATION: if a line was added online (real server id recorded)
   *  and the device goes offline before it's removed, removeSdrLine's
   *  network call will fail and the server-side line won't actually be
   *  deleted until the rep is back online and removes it again — no
   *  offline-delete-queue exists for an already-synced line (accepted,
   *  matching this feature's "no purge for orphans" scope decision). */
  private productLineIds = new Map<SdrProductLine, string | undefined>();
  /** Last quantity actually SENT for each line, keyed by object identity like
   *  productLineIds. Lets _persistProductQty skip a redundant PATCH when the
   *  value hasn't moved since the previous send — without it, five fast taps
   *  on the stepper issue five identical PATCHes (the FIFO queue collapses
   *  them to the same value, but still pays for every round-trip, which
   *  matters on the low-end devices/poor connectivity reps actually use). */
  private persistedQtys = new Map<SdrProductLine, number>();
  /** Pending debounce timer per line (see QTY_PERSIST_DEBOUNCE_MS). Cleared on
   *  destroy, on that line's removal, and at the top of submit() — a timer
   *  that survived finalize would PATCH a qty onto an already-Submitted
   *  record. */
  private qtyPersistTimers = new Map<SdrProductLine, ReturnType<typeof setTimeout>>();
  /** Server-side annotation id (online) per staged note, keyed by OBJECT
   *  IDENTITY (the exact StagedSdrNote instance), same rationale as
   *  productLineIds — distinguishes a removed-then-re-added note from a new
   *  one. Set only when a note was added ONLINE and its upsertSdrNote call
   *  resolved successfully. */
  private noteServerIds = new Map<StagedSdrNote, string>();
  /** Built by _initResumeState() when resuming an existing Draft SDR — bound
   *  in the template to SdrNotesSectionComponent's initialStagedNotes input.
   *  Empty/undefined for a brand-new draft (nothing to seed). */
  _seededNotes: StagedSdrNote[] | undefined;
  /** Resolves once the draft header exists (create-on-New has committed),
   *  so a save-on-click call triggered before that finishes (e.g. a very fast
   *  tap on the product picker right after New) waits for it instead of
   *  racing it. */
  private _draftReady: Promise<void> = Promise.resolve();
  /** Serializes every save-on-click call onto this draft — a self-contained
   *  queue, not shared with inline-allocation-order.ts's own persist queue
   *  (Sample Drop's equivalent, a deliberately separate implementation). */
  private _persistQueue: Promise<void> = Promise.resolve();

  private _busyCount = 0;
  /** True whenever a save-on-click operation (enqueued via _enqueuePersist)
   *  is currently in flight for this draft. Backed by a counter (not a plain
   *  toggle) so multiple independent async chains can each contribute their
   *  own begin/end pair without racing each other's reset. The parent's
   *  "+New" button binds its [disabled] to this — it blocks opening a new
   *  draft while THIS draft has an unfinished edit in flight, but does NOT
   *  stay true just because a draft exists/is open (reps can legitimately
   *  create multiple separate SDRs per meeting). Record creation itself no
   *  longer happens here — the parent (createNewInlineSdr) creates the
   *  header before this component ever mounts — so this no longer covers
   *  that window either. */
  get isBusy(): boolean {
    return this._busyCount > 0;
  }

  private _beginBusy(): void {
    this._busyCount++;
    this.cd.markForCheck();
  }

  private _endBusy(): void {
    this._busyCount--;
    this.cd.markForCheck();
  }

  /** Enqueue one save-on-click operation, serialized after every previously
   *  queued one (and after the draft itself exists). Failures are logged, not
   *  rethrown — a save-on-click hiccup must not crash the form; the rep keeps
   *  editing and finalizeSdr() (a later task) is still the authoritative save
   *  point for what actually reached the server. */
  private _enqueuePersist(op: () => Promise<void>): void {
    this._beginBusy();
    this._persistQueue = this._persistQueue
      .then(() => this._draftReady)
      .then(op)
      .catch((err) => console.error('InlineSampleDeliveryRequestComponent: save-on-click failed', err))
      .finally(() => {
        this._endBusy();
      });
  }

  /**
   * Mirrors this.sdr.products with the current formState.products and re-seeds
   * the app-wide allSdrs cache via the same cacheCreatedSdr helper createNewInlineSdr/
   * finalizeSdr already use — call after every successful save-on-click product
   * mutation (add/remove/qty change).
   *
   * Without this, allSdrs — and therefore activeSdrsInMeeting/the parent's [sdr]
   * input on a FRESH mount — stays frozen at whatever products existed at the
   * draft's creation instant (always none: createNewInlineSdr's payload has no
   * products yet) until a successful Submit ever runs (cacheCreatedSdr's own
   * OMNI-61497 doc comment describes this exact gap for the whole-record case).
   * Each individual product add/remove/qty-change IS already durably persisted
   * to Dynamics/the offline pending-payload via dataService above — this was a
   * client-side cache-staleness gap, not data loss — but it stayed invisible as
   * long as the form kept re-opening editable (a rep who noticed missing
   * products could just re-add them and eventually Submit). Once a meeting goes
   * Cancelled, isReadOnly locks the form before Submit is ever reachable, so a
   * fresh mount's read-only view (which reads this.sdr.products directly, not
   * formState) permanently showed an empty list despite the real record already
   * having the correct lines.
   */
  private async _syncSdrProductsCache(): Promise<void> {
    if (!this.sdr?.id) return;
    this.sdr.products = [...this.formState.products];
    try {
      await this.sdrService.cacheCreatedSdr(this.sdr);
    } catch (err) {
      console.error('InlineSampleDeliveryRequestComponent: failed to sync products to allSdrs cache', err);
    }
  }

  /**
   * Same gap as _syncSdrProductsCache above, for contact/address/email:
   * _persistContactDetails durably saves the current formState to
   * Dynamics/the offline pending doc, but never wrote the result back onto
   * `this.sdr` — the very object referenced by allSdrs/resumingSdr that a
   * fresh mount of this component (tab switch away-and-back, or reopening
   * the record) re-hydrates formState FROM. So a manually-typed alternate
   * address/email (or a picked saved one) reached the server correctly but
   * still appeared to vanish on revisit, because the cache the next mount
   * reads from was never updated. Call this after every successful
   * _persistContactDetails save, mirroring the products pattern exactly.
   */
  private async _syncSdrContactCache(): Promise<void> {
    if (!this.sdr?.id) return;
    this.sdr.contactId = this.formState.contactId;
    this.sdr.contactName = this.formState.contactName;
    this.sdr.deliveryAddress = this.formState.useAlternateAddress ? '' : this.formState.deliveryAddressId;
    this.sdr.deliveryAddressName = this.formState.useAlternateAddress ? '' : this.formState.deliveryAddressLabel;
    this.sdr.email = this.formState.useAlternateEmail ? '' : this.formState.emailId;
    this.sdr.emailAddress = this.formState.useAlternateEmail ? '' : this.formState.emailLabel;
    this.sdr.alternateAddress = this.formState.useAlternateAddress ? this.formState.alternateDeliveryAddress : '';
    this.sdr.alternateEmail = this.formState.useAlternateEmail ? this.formState.alternateEmail : '';
    try {
      await this.sdrService.cacheCreatedSdr(this.sdr);
    } catch (err) {
      console.error('InlineSampleDeliveryRequestComponent: failed to sync contact/address/email to allSdrs cache', err);
    }
  }

  /**
   * Same gap as _syncSdrProductsCache/_syncSdrContactCache above, for notes:
   * onStagedNoteAdded/onStagedNoteEdited/onStagedNoteRemoved durably persist
   * each note to Dynamics/the offline pending doc, but never wrote the
   * result back onto `this.sdr.notes` — so a fresh mount (Materials/Drops
   * sub-tab round trip) re-seeded `_seededNotes` from that stale
   * `this.sdr.notes` via `_buildSeededNotes()`, making an added/edited note
   * appear to vanish even though it was already saved. `stagedNotesView` is
   * SdrNotesSectionComponent's own IONote-shaped mirror of `staged`, kept in
   * lockstep by array index (already relied on the same way at submit time,
   * see the `saved.notes = this.notesSection?.stagedNotesView` assignment
   * below). Call this AFTER the persist call succeeds, from inside the same
   * enqueued op, so it reads `staged`/`stagedNotesView` once the child's own
   * mutation (push/splice) for this event has already happened.
   */
  /**
   * IONote mirror of the composer's staged notes that preserves each note's
   * IDENTITY, not just its display fields.
   *
   * `stagedNotesView` is the child's own view model: its `noteId` is a synthetic
   * `staged-<index>` stamp (reassigned on every splice), and a note's `clientId`
   * appears nowhere on it. Mirroring it verbatim onto `this.sdr.notes` — which is
   * the very object the parent holds as `resumingSdr` and re-binds after a
   * Materials/Drops sub-tab round trip — meant the next mount's
   * `_buildSeededNotes` could recover neither an annotation id nor a clientId.
   * A subsequent remove then matched no branch in `onStagedNoteRemoved` and
   * silently deleted nothing, so the note still uploaded on reconnect.
   *
   * Emitting the real annotation id (or the clientId) with `pendingPushForDynamics`
   * set makes this mirror indistinguishable from what
   * `SampleDeliveryRequest.overlayPendingPayload` builds off disk, so a remount
   * resolves identically whichever source it gets. Clones rather than mutating the
   * child's own view objects, which it re-stamps on splice.
   */
  private _mirrorStagedNotes(): IONote[] {
    const staged = this.notesSection?.staged ?? [];
    return (this.notesSection?.stagedNotesView ?? []).map((view, index) => {
      const source = staged[index];
      const persistedId = source?.existingAnnotationId ?? (source ? this.noteServerIds.get(source) : undefined);
      const clone = new IONote({ ...view });
      // A note whose create is still in flight has neither id yet; leaving the
      // synthetic view id in place is correct — there is nothing to remove for
      // it, and _buildSeededNotes refuses to read a `staged-` id as a clientId.
      clone.noteId = persistedId ?? source?.clientId ?? view.noteId;
      clone.pendingPushForDynamics = !persistedId;
      return clone;
    });
  }

  private async _syncSdrNotesCache(): Promise<void> {
    if (!this.sdr?.id) return;
    this.sdr.notes = this._mirrorStagedNotes();
    try {
      await this.sdrService.cacheCreatedSdr(this.sdr);
    } catch (err) {
      console.error('InlineSampleDeliveryRequestComponent: failed to sync notes to allSdrs cache', err);
    }
  }

  // Per-section touched flags — invalid chrome only after the user engages a
  // section AND leaves it empty (mirrors the full-page create form).
  touched = {
    contact: false,
    deliveryAddress: false,
    email: false,
    products: false,
  };

  private _qtyDropdownEl: HTMLElement | null = null;
  private _qtyDropdownCleanup: (() => void) | null = null;
  private readonly _destroy$ = new Subject<void>();
  private _parentIonContent: HTMLIonContentElement = null;

  constructor(
    private readonly translate: TranslateService,
    private readonly notificationService: NotificationService,
    public readonly sdrService: SampleDeliveryRequestService,
    private readonly renderer: Renderer2,
    private readonly ngZone: NgZone,
    private readonly contactService: ContactOfflineService,
    private readonly navService: NavigationService,
    private readonly device: DeviceService,
    private readonly cd: ChangeDetectorRef,
    private readonly dataService: SampleDeliveryRequestDataService,
    private readonly activityService: ActivityService,
    private readonly events: EventsService,
    private readonly elRef: ElementRef,
  ) {
    // Contact addresses/emails are merged onto the live Contact object
    // asynchronously by the linked-entity Web Worker. createNewInlineSdr
    // (activities-details-pane.ts) can create this Draft right after initial
    // sync/refresh data, before that merge finishes, leaving deliveryAddress/
    // email blank on the record even though the contact does have one on
    // file — previously only a delta sync re-ran the auto-populate and
    // picked it up (same class of bug OMNI-60589 fixed for the My Customers
    // table). Re-derive once the worker catches up.
    this.events
      .observe(EventName.FINISHEDSERVICEWORKERMAPPING)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        // Invalidate BEFORE the backfill, and outside it: an address the memo
        // could not resolve earlier (empty addressesList) becomes resolvable
        // once the worker lands, and _reapplyContactDetailsIfMissing bails
        // early on a read-only mount, which is exactly where the read-only
        // address getter reads through this memo.
        this._addressLabelCache = null;
        this._reapplyContactDetailsIfMissing();
        this.cd.markForCheck();
      });
  }

  ngOnInit(): void {
    this._initState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sdr'] && !changes['sdr'].firstChange) {
      this._initState();
    } else if (
      changes['isReadOnly'] &&
      !changes['isReadOnly'].firstChange &&
      changes['isReadOnly'].previousValue &&
      !this.isReadOnly
    ) {
      // Reopening a Completed meeting flips isReadOnly true->false WITHOUT
      // touching `sdr`, so the clause above never fires. The read-only mount
      // returned early from _initState having built no formState at all (it
      // renders a summary straight off the record), which left the
      // now-visible editable form bound to an undefined formState — every
      // field blank until the component was destroyed and rebuilt by
      // navigating to another meeting and back (OMNI-61614). `else if` so a
      // cycle that changes both inputs still only initializes once.
      this._initState();
    }
    if (changes['meetingContacts'] && !changes['meetingContacts'].firstChange) {
      // Late-arriving single meeting contact auto-selects into an empty draft
      // (this also rebuilds contactFormField via applyContact). Gated on
      // formState.contactId, NOT sdr?.id — createNewInlineSdr() creates the
      // header immediately, with a real sdr.id, before any contact is ever
      // picked (multi/zero-contact meeting), so an sdr?.id check here would
      // permanently block auto-select for every already-created Draft, even
      // one still genuinely contact-less. formState.contactId is the correct
      // "has a contact actually been assigned yet" signal.
      if (!this.formState?.contactId) {
        this._autoSelectSingleContact();
      }
      // Keep the picker's "meeting contacts" section current either way.
      this._buildContactFormField();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._destroyQtyDropdown();
    this.qtyPersistTimers.forEach((timer) => clearTimeout(timer));
    this.qtyPersistTimers.clear();
  }

  /**
   * An explicit read-only flag (or a submit in flight) locks all editable
   * affordances. NOTE: this deliberately does NOT also check `!!this.sdr?.id`
   * — that clause was correct back when every existing-SDR-with-an-id was
   * necessarily read-only (there was no resume case). Now that a Draft
   * (statusCode Active) SDR can carry a real `sdr.id` while `isReadOnly` is
   * false (resumed for editing — see `_initResumeState`), that clause would
   * incorrectly lock the form regardless of `isReadOnly`.
   */
  get isLocked(): boolean {
    return this.isReadOnly || this.isSubmitting;
  }

  // ──────────────── State init ────────────────

  private _initState(): void {
    if (this.sdr?.id) {
      if (this.isReadOnly) {
        // Read-only view — the template renders a summary from the SDR directly,
        // so no editable formState is needed. The object handed in here is
        // usually the SLIM summary from fetchSdrsForMeeting (no notes/attachments
        // — see that method's own doc comment), so hydrate the full record once.
        this._hydrateNotesIfNeeded();
        return;
      }
      // Not read-only but a real sdr.id was handed in — the parent is
      // resuming an existing, not-yet-submitted (statuscode Active) SDR for
      // editing (Resume-Task E wires this up on the caller side). Rebuild a
      // fully editable formState from the persisted record.
      if (this.draftSdrId === this.sdr.id) {
        // Already resumed this exact SDR once. A stray ngOnChanges re-fire
        // (e.g. the parent's `resumingSdr` briefly going null between
        // finalize and its post-save refresh resolving, which swaps the
        // bound `sdr` reference to the still-stale, notes-less list row
        // without `selectedSdrId` ever changing) must NOT rebuild state
        // here: `_initResumeState()` would wipe productLineIds/noteServerIds
        // and reseed them from that stale sdr's empty `notes`, orphaning the
        // tracking for already-persisted notes on the still-live, already-
        // initialized component instance. The parent's onSdrTabChange
        // re-click guard covers tab-switch reentrancy; this covers the
        // same-instance reentrancy that guard can't see.
        return;
      }
      this._initResumeState();
      return;
    }
    this.limitsMap = this.sdrService.getProductLimitsMap();

    const single = this.meetingContacts?.length === 1 ? this.meetingContacts[0] : null;
    this.selectedContact = single;
    this._rebuildOptionsFromContact();

    const primaryAddress = single?.primaryAddress;
    const primaryEmail = single?.emailAddressList?.find((e) => e.isPrimary);
    this.formState = this.sdrService.initFormState(
      single?.ID ?? '',
      single?.fullName ?? '',
      primaryAddress?.addressId ?? '',
      primaryAddress?.compositeAdd ?? '',
      primaryEmail?.emailAddressId ?? '',
      primaryEmail?.emailAddress ?? '',
    );
    // Only default into free-text mode when a contact IS selected but has no
    // saved address/email of its own — never when there's no contact at all
    // (multi-contact meeting, none auto-selected yet), which would otherwise
    // let the rep type an address/email before ever picking a customer.
    if (single && this.addressOptions.length === 0) this.formState.useAlternateAddress = true;
    if (single && this.emailOptions.length === 0) this.formState.useAlternateEmail = true;
    this._buildContactFormField();
    this.cd.markForCheck();

    // Fire-and-forget: refresh the Manual-Distribution allocation cache for
    // this contact (OMNI-59706 parity with the full-page SDR form).
    void this._refreshAllocationsForCurrentContact();
  }

  /**
   * Resume an existing, not-yet-submitted (statusCode Active) SDR for
   * editing — the parent passes a real `sdr` input with `isReadOnly=false`
   * for exactly this case (a Draft-status item in the existing-SDR tab
   * list, including one just created via createNewInlineSdr). Rebuilds a
   * fully editable formState from the already-persisted record, sets
   * draftSdrId directly (the header already exists — the parent always
   * creates it before this component mounts), and re-populates the
   * save-on-click tracking (productLineIds, and the notes composer's seeded
   * staged list + noteServerIds) so further edits correctly target the
   * existing real records rather than creating duplicates.
   *
   * IMPORTANT PRECONDITION for whichever task wires the caller: `this.sdr`
   * must already be the FULLY hydrated record (`notesLoaded === true`, e.g.
   * via `fetchSdrById`/`SampleDeliveryRequestDataService.fetchSdrById`) —
   * NOT the slim summary row `fetchSdrsForMeeting`/`fetchSdrsForAppointment`
   * return for the Request tab's list (that query has no note/annotation
   * join at all, so `sdr.notes` is always `[]` on it; product qty/lineId ARE
   * present on that slim row, so only notes are at risk). Unlike the
   * existing read-only branch's `_hydrateNotesIfNeeded` (which can safely
   * patch `this.sdr.notes` in asynchronously after the fact, because the
   * read-only template just re-renders from the same `sdr` object),
   * this method's `_seededNotes` feeds `SdrNotesSectionComponent`'s
   * `initialStagedNotes` input, which that component reads ONLY ONCE in its
   * own `ngOnInit` (by design — see its own doc comment). By the time an
   * async note-hydration here could resolve, that child has already
   * initialized with whatever `_seededNotes` held at creation — an
   * after-the-fact patch would be silently ignored. So notes hydration MUST
   * happen synchronously before this component (and its child) mount, not
   * inside this method.
   */
  private _initResumeState(): void {
    // Cheap insurance against stale entries: this method is only meant to
    // run once per mount (the parent's onSdrTabChange re-click guard keeps a
    // resumed instance from being torn down and rebuilt against itself), but
    // clearing before repopulating means a future code path that bypasses
    // that guard can't leave orphaned productLineIds/noteServerIds entries
    // keyed to a previous instance's now-discarded objects.
    this.productLineIds.clear();
    this.persistedQtys.clear();
    this.noteServerIds.clear();
    this.limitsMap = this.sdrService.getProductLimitsMap();
    this.draftSdrId = this.sdr.id;

    // KNOWN LIMITATION (accepted, not a crash): if both lookups below fail, or
    // resolve to a partially-hydrated Contact (this codebase has a documented
    // recurring scenario where a global-search contact is missing
    // addressesList/emailAddressList), addressOptions/emailOptions built by
    // _rebuildOptionsFromContact just below can come back empty/short. The
    // CURRENT address/email still display correctly regardless — they're
    // sourced directly from the SDR record's own fields a few lines down, not
    // from this resolved Contact object — but the rep won't be able to switch
    // to a DIFFERENT saved address/email for this resumed draft (no options to
    // pick from) without first re-picking the contact via the full picker
    // (openContactList), which re-resolves against the full Contact List.
    const existingContact =
      this.meetingContacts?.find((c) => c.ID === this.sdr.contactId) ??
      this.contactService.getContactByID(this.sdr.contactId) ??
      null;
    this.selectedContact = existingContact;
    this._rebuildOptionsFromContact();

    this.formState = this.sdrService.initFormState(
      this.sdr.contactId ?? '',
      this.sdr.contactName ?? '',
      this.sdr.deliveryAddress ?? '',
      // Re-resolve the composite rather than trusting the persisted label — see
      // _resolveSavedAddressLabel. This is the point the truncation loop closed:
      // seeding from deliveryAddressName put the address record's bare NAME into
      // formState, which _syncSdrContactCache then persisted back (OMNI-62537).
      this._resolveSavedAddressLabel(this.sdr.contactId, this.sdr.deliveryAddress)
        || (this.sdr.deliveryAddressName ?? ''),
      this.sdr.email ?? '',
      this.sdr.emailAddress ?? '',
    );
    // _rebuildOptionsFromContact (called above, via selectedContact) already
    // forces useAlternateAddress/useAlternateEmail from whether the contact
    // HAS saved addresses/emails at all — override that here with what was
    // ACTUALLY saved on the persisted record, rather than re-deriving from
    // current address/email option availability (unlike the brand-new-draft
    // path above, which legitimately defaults to alternate when the contact
    // has no saved addresses/emails at all — here we must trust the
    // persisted record's own choice, since that's what's really stored).
    this.formState.useAlternateAddress = !!this.sdr.alternateAddress;
    this.formState.alternateDeliveryAddress = this.sdr.alternateAddress ?? '';
    this.formState.useAlternateEmail = !!this.sdr.alternateEmail;
    this.formState.alternateEmail = this.sdr.alternateEmail ?? '';
    // Fallback for the partial-hydration gap noted above: if the resolved
    // Contact came back with no usable saved address/email, the "saved" row
    // has nothing to open (options list is empty) and the persisted record
    // didn't use alternate address/email either — that combination left the
    // field a dead end with no UI path to enter one. Mirrors the standalone
    // create form's post-formState defaulting (new-sample-delivery-request.component.ts).
    // Guarded on selectedContact — a Draft that has no contact at all yet
    // (multi-contact meeting, never picked) must NOT fall into free-text
    // mode either, or the rep could type an address/email before choosing a
    // customer. ALSO guarded on the record having no saved address/email of
    // its own (formState.deliveryAddressId/emailId empty) — a resumed Draft
    // whose persisted record DOES carry a saved address/email must keep
    // showing it even when the currently-resolved Contact's addressOptions/
    // emailOptions come back empty (offline partial-hydration: a global-
    // search/meeting contact missing addressesList/emailAddressList).
    // Without this guard, that hydration gap alone force-flipped a
    // perfectly valid saved address/email into the empty free-text box the
    // instant the Contact object wasn't fully loaded yet (OMNI-60544).
    if (this.selectedContact && this.addressOptions.length === 0 && !this.formState.deliveryAddressId) {
      this.formState.useAlternateAddress = true;
    }
    if (this.selectedContact && this.emailOptions.length === 0 && !this.formState.emailId) {
      this.formState.useAlternateEmail = true;
    }

    // Clone each product line into a fresh object (never reuse this.sdr's own
    // array/objects — formState.products must own independent instances,
    // since productLineIds tracks by object identity and later edits mutate
    // these objects in place). minQty/maxQty/hasLimit are deliberately
    // RECOMPUTED here (not copied from this.sdr.products) — those come from
    // groupSdrRows, which hard-codes minQty: 0, maxQty: null and never sets
    // hasLimit when building product rows from a fetch (it has no access to
    // this.limitsMap). Without this recompute, a resumed line for a SKU with
    // a configured limit would silently lose all qty-range enforcement (the
    // stepper's isDecrementDisabled/isIncrementDisabled and
    // sdrService.getProductQtyError — used by both the inline error chip and
    // validateForSubmit's pre-finalize gate — all read these same fields).
    // Mirrors addSkuToForm's own use of getSkuMinQty/getSkuMaxQty/hasSkuLimit
    // for a freshly-added line.
    this.formState.products = (this.sdr.products ?? []).map((p) => ({
      ...p,
      minQty: this.getSkuMinQty(p.skuId),
      maxQty: this.getSkuMaxQty(p.skuId),
      hasLimit: this.hasSkuLimit(p.skuId),
    }));
    for (const product of this.formState.products) {
      if (product.lineId) {
        this.productLineIds.set(product, product.lineId);
      } else {
        console.warn(`[SDR-REMOVE] _initResumeState: product skuId=${product.skuId} on sdrId=${this.sdr.id} has NO lineId from the fetch — a later removeProduct for this SKU will wrongly take the offline-local-removal branch even online`);
      }
      // The qty we just loaded IS what's stored — record it so neither an
      // edit-then-revert nor submit()'s pre-finalize sweep re-sends a value
      // the server already has.
      this.persistedQtys.set(product, product.qty);
    }

    this._seededNotes = this._buildSeededNotes(this.sdr.notes ?? []);

    this._buildContactFormField();
    this.cd.markForCheck();

    // Force-bypass the TTL cache: this fires every time a Draft SDR is
    // (re)opened — including every "pill" switch under Samples and
    // Materials, since that destroys and recreates this component — which
    // is exactly the moment a stale "remaining" figure is most likely to
    // cause a bad add/submit.
    void this._refreshAllocationsForCurrentContact(true);
  }

  /**
   * Converts an existing SDR's already-real notes into the shape
   * SdrNotesSectionComponent's `initialStagedNotes` input expects, and
   * records each one's real annotation id in `noteServerIds` (keyed by the
   * exact object instance handed to the child) — so removing one of these
   * later correctly calls removeSdrNote(realId) instead of mistaking it for
   * an offline-only note. Attachment metadata (filename/mimetype/size) is
   * carried over for display; documentBody is intentionally left empty —
   * these notes are already fully persisted, so their content is never
   * resubmitted; only removal (or, in a future task, an edit-resync) needs
   * to reference them.
   */
  private _buildSeededNotes(notes: IONote[]): StagedSdrNote[] {
    const seeded: StagedSdrNote[] = [];
    for (const note of notes) {
      const noteId = note.noteId ?? '';
      // A note staged offline has no annotation id yet — its id is the
      // clientId SampleDeliveryRequest.overlayPendingPayload carried over from
      // the pending doc (or a synthetic `staged-<n>` view id, if this seed came
      // from the in-memory allSdrs mirror rather than disk). Registering either
      // as a server id would point a later edit/remove at a Dynamics record
      // that does not exist, and would skip the offline sibling that actually
      // owns the note — so route those to clientId instead.
      // A `staged-<n>` id is the notes composer's own display stamp, reassigned
      // on every splice — never an identity. It must become neither a server id
      // nor a clientId; only a note whose create is still in flight can carry
      // one, and there is nothing to remove for such a note anyway.
      const isSyntheticId = noteId.startsWith('staged-');
      const isPersisted = !!noteId && !note.pendingPushForDynamics && !isSyntheticId;
      const staged: StagedSdrNote = {
        noteText: note.noteText ?? '',
        // Lets SdrNotesSectionComponent.toIoNoteView use the REAL annotation
        // id (instead of a synthetic staged-<index> id) for this already-
        // persisted note, so its attachment (if any) is downloadable — a
        // synthetic id would send fileService.getDocumentBody a bogus GUID.
        existingAnnotationId: isPersisted ? noteId : undefined,
        clientId: !isPersisted && !isSyntheticId && note.pendingPushForDynamics ? noteId : undefined,
        attachment: note.hasDocument
          ? {
              // Empty for an already-persisted note: its content is never
              // resubmitted, only referenced. An offline-staged one has no
              // server copy to fetch from, so its body must survive the round
              // trip or the attachment resumes as a filename with no file.
              documentBody: isPersisted ? '' : (note.documentDataURL ?? ''),
              fileName: note.documentName ?? '',
              mimeType: note.documentMimeType ?? '',
              fileSize: note.documentSize ?? 0,
            }
          : undefined,
      };
      if (isPersisted) this.noteServerIds.set(staged, noteId);
      seeded.push(staged);
    }
    return seeded;
  }

  /** Now, not the meeting's date — GetAllocationConfiguration checks the SDR's
   *  `createdon`; anything else is rejected at submit (OMNI-61513). */
  private get _allocationAsOf(): Date {
    return new Date();
  }

  /** Re-syncs the Manual-Distribution allocation cache for the form's current
   *  contact and re-resolves allocationRemaining/allocationUnlimited/
   *  allocationNotEligible on every already-added product line — mirrors
   *  NewSampleDeliveryRequestComponent._refreshAllocationsForCurrentContact. */
  private async _refreshAllocationsForCurrentContact(forceRefresh = false): Promise<void> {
    const contactId = this.formState?.contactId;
    if (!contactId) return;
    await this.sdrService.syncManualAllocationsForContact(contactId, forceRefresh);
    for (const product of this.formState?.products ?? []) {
      this.sdrService.applyAllocationDisplay(product, this._resolveAllocationDisplay(contactId, product.skuId));
    }
    this.cd.markForCheck();
  }

  /**
   * Resolves the three allocation-derived display fields for one SKU.
   * Deliberately blanks remaining/unlimited whenever isAllocationBlocking is
   * true (expired config, ineligible customer, or no config at all) — a
   * stale "Remaining: N" chip next to the "not currently available" warning
   * would flatly contradict it. Mirrors
   * NewSampleDeliveryRequestComponent._resolveAllocationDisplay.
   *
   * Both calls below MUST share one asOf, or the picker offers a SKU the add
   * path then marks allocationNotEligible — which it treats as confirmed
   * doomed, skipping the save outright (OMNI-61513).
   */
  private _resolveAllocationDisplay(contactId: string, skuId: string): SdrAllocationDisplay {
    return this.sdrService.resolveAllocationDisplay(contactId, skuId, this._allocationAsOf, this._meetingWindow);
  }

  /**
   * Slim summaries (`SampleDeliveryRequestService.fetchSdrsForMeeting`'s
   * timeline query) arrive WITHOUT notes/attachments by design — only the
   * full-detail (`fetchSdrById`) and create paths carry them. Mirrors
   * `SampleDeliveryRequestDetailComponent`'s `notesLoaded` gate (OMNI-57234)
   * so the Request tab's read-only row shows real notes/attachments instead
   * of always rendering an empty section.
   *
   * Guarded by `_notesHydratedForId` (an id, not `target.notesLoaded`) — the
   * parent (activities-details-pane.ts) reassigns `sdrsInSelectedMeeting`
   * from up to three independent sources on meeting open (a one-shot fetch
   * plus a BehaviorSubject subscription that itself re-fires from that same
   * fetch's internal update), each producing a BRAND-NEW object instance for
   * the same SDR id. `@for`'s `[sdr]` binding change-detects each of those
   * as a changed input on the same persisted child instance, re-firing
   * ngOnChanges → _initState → this method every time. `target.notesLoaded`
   * lives on the (transient) object instance, so it can't prevent this;
   * tracking by id at the component level does.
   */
  private _notesHydratedForId: string | null = null;

  /** Notes already fetched for `_notesHydratedForId`, kept so a LATER object
   *  instance for the same id can be re-seeded without another round trip. */
  private _hydratedNotes: IONote[] = [];

  /**
   * Graft the notes fetched for `fetchedFor` onto whatever instance is bound now,
   * provided it is still the same SDR. Merges rather than replacing, matching the
   * offline branch's own rule — a newer instance carries fresher summary fields
   * (status/address/email/products) that a wholesale swap would discard.
   */
  private _applyHydratedNotes(fetchedFor: SampleDeliveryRequest): void {
    if (!this.sdr || this.sdr.id !== fetchedFor.id) return;
    this.sdr.notes = this._hydratedNotes;
    this.sdr.notesLoaded = true;
    this.cd.markForCheck();
  }

  private async _hydrateNotesIfNeeded(): Promise<void> {
    const target = this.sdr;
    if (!target?.id || target.notesLoaded) return;
    if (this._notesHydratedForId === target.id) {
      // Already fetched for this id, but `sdr` is a DIFFERENT, slim instance now
      // — a delta sync replaces the record with one from fetchAllForRep, which
      // has no annotation join (notes: [], notesLoaded: false). The id guard
      // alone then blocked re-hydration forever and the section rendered empty
      // (OMNI-61595). Re-seed from what was already fetched instead of paying
      // for another round trip, which is what the guard exists to prevent.
      if (this._hydratedNotes.length) {
        target.notes = this._hydratedNotes;
        target.notesLoaded = true;
        this.cd.markForCheck();
      }
      return;
    }
    this._notesHydratedForId = target.id;
    try {
      if (this.device.isOffline) {
        // Offline: serve notes from the lazy detail cache if this SDR was ever
        // opened online (or created on-device). Merge onto the existing
        // object — never replace — so the fresh summary fields (status/
        // address/email/products) survive; only notes/attachments are grafted.
        const cached = await this.dataService.getCachedSdrRecord(target.id);
        if (cached) {
          // Keyed by ID, not object identity. The parent reassigns
          // sdrsInSelectedMeeting from several sources on meeting open, each a
          // NEW instance for the same id (see this method's doc comment), so
          // `this.sdr` routinely changes while this await is in flight.
          // Recording the result only when the instance still matched threw the
          // notes away in exactly that case — and since _notesHydratedForId was
          // already set, nothing ever retried, so the section stayed empty until
          // the component was destroyed and rebuilt by switching SDR chips.
          this._hydratedNotes = cached.notes ?? [];
          this._applyHydratedNotes(target);
        }
        return;
      }
      // Online: show whatever's already cached locally FIRST (tentative —
      // notesLoaded deliberately left false), so the notes section isn't
      // visibly empty for the whole network round-trip. The network fetch
      // below then replaces it with authoritative data once it resolves,
      // same local-first-then-replace principle as fetchSdrsForMeeting.
      // getCachedSdrRecord is a fast local disk read regardless of
      // connectivity, so this costs nothing extra beyond the offline branch
      // already pays.
      const cached = await this.dataService.getCachedSdrRecord(target.id);
      if (cached?.notesLoaded && this.sdr === target) {
        this.sdr.notes = cached.notes ?? [];
        this.cd.markForCheck();
      }
      const full = await this.dataService.fetchSdrById(target.id);
      if (full) {
        this._hydratedNotes = full.notes ?? [];
        await this.dataService.saveSdrRecordToDb(full);
        if (this.sdr === target) {
          // Same instance still bound: replace wholesale, as before, so the
          // authoritative server record wins.
          this.sdr = full;
          this.cd.markForCheck();
        } else {
          this._applyHydratedNotes(target);
        }
      }
    } catch (err) {
      console.error('InlineSampleDeliveryRequestComponent: hydrate notes failed', err);
    }
  }

  private _autoSelectSingleContact(): void {
    if (this.meetingContacts?.length === 1) {
      this.applyContact(this.meetingContacts[0]);
    }
  }

  // ──────────────── Customer ────────────────

  get customerName(): string {
    // Same rationale as isLocked: only a genuinely read-only view should show
    // the static sdr snapshot. A resumed Draft (sdr.id set, isReadOnly=false)
    // must track live formState.contactName edits like a brand-new draft does.
    return this.isReadOnly && this.sdr?.id ? (this.sdr.contactName || '') : (this.formState?.contactName || '');
  }

  /**
   * Open the full Contact List (SELECTION mode) to pick / change the customer —
   * mirrors the Sample-Drop in-meeting flow. The existing contact-list SDR
   * branch (accessedContactListFrom === NewSampleDeliveryRequestComponent)
   * invokes the callbackEvent with the chosen contact.
   */
  openContactList(): void {
    if (this.isLocked) return;
    this.contactService.contactPageMode = ComponentViewMode.SELECTION;
    this.contactService.accessedContactListFrom = PageName.NewSampleDeliveryRequestComponent;
    const callbackEvent = (data: any) => {
      if (Array.isArray(data) && data.length > 0) {
        const contact = data[0] as Contact;
        if (contact?.ID && contact.ID !== this.formState?.contactId) {
          this.applyContact(contact);
          this.onContactSelected.emit({ contact });
        }
      }
    };
    this.navService.pushWithPageTracking(
      PageName.ContactPageComponent,
      PageName.ContactPageComponent,
      { callbackEvent, selectedContactId: this.formState?.contactId ?? null },
      PageName.ContactPageComponent,
    );
  }

  /** Apply a chosen contact to the draft: rebuild address/email options + defaults. */
  applyContact(contact: Contact): void {
    if (this.isLocked || !this.formState) return;
    this.selectedContact = contact;
    this._rebuildOptionsFromContact();
    const primary = contact.primaryAddress;
    const primaryEmail = contact.emailAddressList?.find((e) => e.isPrimary);
    this.formState.contactId = contact.ID;
    this.formState.contactName = contact.fullName ?? '';
    this.formState.deliveryAddressId = primary?.addressId ?? '';
    this.formState.deliveryAddressLabel = primary?.compositeAdd ?? '';
    this.formState.emailId = primaryEmail?.emailAddressId ?? '';
    this.formState.emailLabel = primaryEmail?.emailAddress ?? '';
    this._buildContactFormField();
    this.cd.markForCheck();

    this._persistContactDetails();

    // Swapping the customer means any already-added product lines' allocation
    // info (resolved against the PREVIOUS contact) is now stale — re-sync for
    // the new contact and re-resolve it (OMNI-59706 parity, see
    // NewSampleDeliveryRequestComponent.applyContactChange).
    void this._refreshAllocationsForCurrentContact();
  }

  /** Save-on-click: patch the draft's current contact/address/email onto the
   *  Draft. Called by applyContact (new contact) AND by every subsequent
   *  address/email edit on their own (selectAddress/selectEmail,
   *  onAlternateAddressBlur/onAlternateEmailBlur) — previously ONLY
   *  applyContact triggered this, so changing the address/email for an
   *  already-selected contact silently never autosaved until the rep
   *  reselected the contact or hit Submit (which best-effort re-sends once
   *  more, but that's the only safety net there was).
   *  Offline: writes straight to the local pending doc via
   *  updateSdrDraftContactOffline (see that method's doc comment — a
   *  contact/address/email pick made offline used to be silently dropped
   *  forever since no offline sibling existed; this is the fix). Reads
   *  formState fresh inside the op (not captured here) so the FIFO queue's
   *  last-enqueued op reflects the last edit, matching this file's
   *  established save-on-click convention (see addSkuToForm/removeProduct). */
  private _persistContactDetails(): void {
    this._enqueuePersist(async () => {
      if (!this.draftSdrId) return;
      const fields = {
        contactId: this.formState.contactId,
        contactName: this.formState.contactName,
        deliveryAddress: this.formState.useAlternateAddress ? undefined : this.formState.deliveryAddressId,
        deliveryAddressLabel: this.formState.useAlternateAddress ? undefined : this.formState.deliveryAddressLabel,
        email: this.formState.useAlternateEmail ? undefined : this.formState.emailId,
        emailLabel: this.formState.useAlternateEmail ? undefined : this.formState.emailLabel,
        alternateAddress: this.formState.useAlternateAddress ? this.formState.alternateDeliveryAddress : undefined,
        alternateEmail: this.formState.useAlternateEmail ? this.formState.alternateEmail : undefined,
      };
      try {
        if (this.device.isOffline) {
          await this.dataService.updateSdrDraftContactOffline(this.draftSdrId, fields);
        } else {
          await this.dataService.updateSdrDraftContact(this.draftSdrId, fields);
        }
        await this._syncSdrContactCache();
      } catch (err) {
        console.error('InlineSampleDeliveryRequestComponent: contact/address/email save-on-click failed', err);
        this.notificationService.notify(
          this.translate.instant('SDR_CONTACT_SAVE_FAILED'),
          'inline-sample-delivery-request',
          'top',
          ToastStyle.DANGER,
        );
      }
    });
  }

  /** Save-on-click hook for SdrNotesSectionComponent's (stagedNoteAdded)
   *  event — creates the note against this draft the instant the rep commits
   *  it via the composer's Add button (online: real annotation; offline:
   *  local pendingFormPayload entry).
   *
   *  KNOWN LIMITATION: an edit to an already-added-and-persisted note (via
   *  the pencil icon, SdrNotesSectionComponent.saveEditStagedNote) is never
   *  re-synced here — only add/remove are wired. If the session ends or an
   *  offline Draft syncs before Submit, stale pre-edit content can reach
   *  Dynamics/local storage. Not fixed in this task; needs its own
   *  save-on-click hook (e.g. a stagedNoteEdited output) in a follow-up. */
  onStagedNoteAdded(event: { index: number; note: StagedSdrNote }): void {
    const noteRef = event.note;
    // Stable id surviving array-position shifts caused by earlier removals —
    // required for removeSdrNoteOffline to target the right note (mirrors
    // removeSdrLineOffline's skuId approach for product lines).
    noteRef.clientId = noteRef.clientId ?? Guid.create().toString();
    this._enqueuePersist(async () => {
      if (!this.draftSdrId) return;
      if (this.device.isOffline) {
        await this.dataService.upsertSdrNoteOffline(this.draftSdrId, noteRef);
        await this._syncSdrNotesCache();
        return;
      }
      try {
        const noteId = await this.dataService.upsertSdrNote(this.draftSdrId, noteRef);
        this.noteServerIds.set(noteRef, noteId);
        await this._syncSdrNotesCache();
      } catch (err) {
        console.error('InlineSampleDeliveryRequestComponent: note save-on-click failed', err);
        this.notificationService.notify(
          this.translate.instant('SDR_NOTE_SAVE_ON_CLICK_FAILED'),
          'inline-sample-delivery-request',
          'top',
          ToastStyle.DANGER,
        );
      }
    });
  }

  /** Save-on-click hook for SdrNotesSectionComponent's (stagedNoteEdited)
   *  event — re-sends a note the rep edited via the pencil icon. Closes the
   *  limitation noted on onStagedNoteAdded above: add/remove autosaved but an
   *  edit never did, so the pre-edit text reached Dynamics while the rep saw
   *  their edit on screen (and unlike qty, submit() has no pre-finalize note
   *  resend to catch it).
   *  Uses updateSdrNote, NOT upsertSdrNote — the latter only ever calls
   *  dynamics.create despite its name, so reusing it here would create a
   *  duplicate annotation instead of updating the original. */
  onStagedNoteEdited(event: { index: number; note: StagedSdrNote; attachmentCleared: boolean }): void {
    const noteRef = event.note;
    if (!noteRef) return;
    this._enqueuePersist(async () => {
      if (!this.draftSdrId) return;
      const serverId = this.noteServerIds.get(noteRef);
      try {
        if (serverId) {
          await this.dataService.updateSdrNote(serverId, noteRef, event.attachmentCleared);
        } else if (noteRef.clientId) {
          // No server id: either added offline, or its add is still in flight /
          // was rejected. Patch the local pending doc — a harmless no-op when
          // nothing was ever staged for this clientId.
          await this.dataService.updateSdrNoteOffline(this.draftSdrId, noteRef);
        }
        await this._syncSdrNotesCache();
      } catch (err) {
        console.error('InlineSampleDeliveryRequestComponent: note edit save-on-click failed', err);
        this.notificationService.notify(
          this.translate.instant('SDR_NOTE_SAVE_ON_CLICK_FAILED'),
          'inline-sample-delivery-request',
          'top',
          ToastStyle.DANGER,
        );
      }
    });
  }

  /** Save-on-click hook for SdrNotesSectionComponent's (stagedNoteRemoved)
   *  event — deletes the note if it had already saved (online or offline)
   *  before the rep removed it. The event only carries a staged-array index,
   *  so the note object itself must be captured HERE, synchronously, before
   *  the child component's own splice runs (EventEmitter.emit is synchronous,
   *  so this handler executes to completion before deleteStagedNote continues
   *  past its own emit call — the child's staged array is still un-spliced
   *  at this exact moment). */
  onStagedNoteRemoved(index: number): void {
    const noteRef = this.notesSection?.staged?.[index];
    if (!noteRef) return;
    this._enqueuePersist(async () => {
      if (!this.draftSdrId) return;
      const serverId = this.noteServerIds.get(noteRef);
      this.noteServerIds.delete(noteRef);
      if (serverId && this.device.isOffline) {
        // Already-synced note, no network to delete it right now — queue it
        // for uploadPendingSdrs to delete on reconnect, same rationale as
        // removeProduct's queueSdrLineDeleteOffline branch.
        await this.dataService.queueSdrNoteDeleteOffline(this.draftSdrId, serverId);
      } else if (serverId) {
        await this.dataService.removeSdrNote(serverId);
      } else if (noteRef.clientId) {
        await this.dataService.removeSdrNoteOffline(this.draftSdrId, noteRef.clientId);
      }
      // else: the add was still in-flight, was rejected, or nothing was ever
      // persisted for this note — nothing to remove.
      await this._syncSdrNotesCache();
    });
  }

  /** Rebuild the ind-form-field viewData — mirrors IAO's contact picker exactly
   *  (search dropdown, meeting-contacts section, "view more"/"search other"
   *  escape hatches to the full Contact List), so the Request tab's Customer
   *  field looks and behaves like its sibling Drops tab and the full-page SDR
   *  form (both of which use ind-form-field for this same field). */
  private _buildContactFormField(): void {
    const isEditable = !this.isLocked;
    const hasContact = !!this.formState?.contactId;

    const endIcons = isEditable ? [
      { iconName: 'searchblue', overRideDisabledProperty: false },
      { iconName: 'expandblue', overRideDisabledProperty: true },
    ] : [];

    this._buildContactSourceData();
    const selectedChipData = this._getSelectedContactData();
    const meetingContactData = this._getMeetingContactData();

    let selectedData: any[];
    if (hasContact) {
      selectedData = selectedChipData;
    } else if (meetingContactData.length > 0) {
      selectedData = meetingContactData;
    } else {
      // No sentinel row when both are empty — a placeholder "gate" item would
      // make ind-form-field.shouldShowDropDown() truthy on focus, rendering an
      // empty bordered dropdown box (matches the full-page SDR form's fix).
      selectedData = [];
    }
    const hideInlineChip = !hasContact;

    const sections = this._generateContactSections(hasContact ? selectedChipData : [], meetingContactData);

    this.contactFormField = {
      label: this.translate.instant('CUSTOMER'),
      inputText: '',
      customPlaceholderLabel: hasContact ? '' : this.translate.instant('SELECT_CUSTOMER'),
      id: 'inline-sdr-contact-field',
      isReadOnly: true,
      showArrow: false,
      // Mandatory marker, not an empty-state hint: Delivery Address and
      // Products keep their asterisk once filled, so Customer dropping its
      // own the moment a contact was picked read as "no longer required"
      // (OMNI-61637). isEmptyRequiredField below is the separate
      // has-the-rep-left-it-blank signal and stays gated on touched.contact.
      isRequired: isEditable,
      // Gated on touched.contact so the error only shows once the rep has
      // actually opened the picker and left it empty — mirrors Delivery
      // Address / Email / Products above, and Sample Drop's _isClickedContact
      // (new-sample-activity.ts), instead of showing on first render.
      isEmptyRequiredField: isEditable && this.touched.contact && !hasContact,
      errorMessage: this.translate.instant('THIS_IS_A_REQUIRED_FIELD'),
      formFieldType: FormFieldType.LIST,
      isDisabled: !isEditable,
      isSearchKeyRetain: true,
      hideInlineChip,
      modalConfiguration: {
        searchTitle: this.translate.instant('SEARCH_CONTACTS'),
        selectedTitle: this.translate.instant('SELECTED_CONTACTS'),
        titleEndIcon: 'expandblue',
        searchInputConfig: {
          label: this.translate.instant('CONTACTS'),
          placeholder: this.translate.instant('TYPE') + '...',
        },
        showSearch: true,
      },
      filterMethod: this._filterContactData.bind(this),
      endIcons,
      sections,
      selectedData,
      eventHandler: (id: string, event: any, eventName: string, data: any) => {
        if (data && eventName === 'item-click') {
          const contact = data.refObject as Contact;
          if (contact?.ID && contact.ID !== this.formState?.contactId) {
            this.applyContact(contact);
            this.onContactSelected.emit({ contact });
          }
        } else if (eventName === 'closebutton') {
          if (hasContact) {
            this.clearCustomer();
          }
        }
      },
    };
  }

  private _buildContactSourceData(): void {
    const selectedId = this.formState?.contactId || '';
    this.contactSourceData = (this.contactService.contacts || [])
      .filter((c) => c.isActive && c.ID !== selectedId)
      .map((contact) => ({
        id: contact.ID,
        title: this._contactDisplayName(contact),
        image: '',
        refObject: contact,
      }))
      .sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));
  }

  private _getSelectedContactData(): any[] {
    if (!this.formState?.contactId) return [];
    const contact = this.selectedContact ?? this._lookupContact(this.formState.contactId);
    const name = contact ? this._contactDisplayName(contact) : (this.formState.contactName || '');
    return [{
      id: this.formState.contactId,
      title: name,
      image: '',
      refObject: contact || null,
      endIcon: 'closebutton',
    }];
  }

  private _getMeetingContactData(): any[] {
    const selectedId = this.formState?.contactId || '';
    return (this.meetingContacts || [])
      .filter((c) => c.ID !== selectedId)
      .map((c) => ({
        id: c.ID,
        title: this._contactDisplayName(c),
        image: '',
        refObject: c,
      }));
  }

  private _filterContactData(query: string): any[] {
    const queryWords = query.toLowerCase().split(' ').filter((w) => w.trim() !== '');
    const selectedId = this.formState?.contactId || '';
    // Deliberately does NOT exclude meeting contacts (unlike the pre-search
    // "meeting contacts" quick-pick section, which is a separate, static list) —
    // a meeting attendee is a perfectly valid SDR customer and must stay
    // findable by search. Excluding them here meant a rep who cleared the
    // customer and re-searched for that very contact (e.g. the one
    // auto-populated from a single-contact meeting) got "no results" the
    // moment they typed anything, since this filterMethod replaces the
    // quick-pick section's display while actively searching (OMNI-61272).
    return this.contactSourceData
      .filter((item) => {
        const title = item.title.toLowerCase();
        return queryWords.every((word) => title.includes(word));
      })
      .filter((item) => item.id !== selectedId);
  }

  private _generateContactSections(selectedSectionData: any[], meetingContactData: any[]): Section[] {
    const sections: Section[] = [
      {
        id: 'selected',
        title: this.translate.instant('SELECTED'),
        showCountOnTitle: true,
        maxResultsView: 5,
        bottomButtonShow: false,
        expand: false,
        showBottomButton: false,
        bottomButtonConfig: {},
        data: selectedSectionData,
      },
    ];

    sections.push({
      id: 'results',
      title: this.translate.instant('AL_RESULTS'),
      showFilteredData: true,
      showCountOnTitle: true,
      maxResultsView: 5,
      bottomButtonShow: true,
      expand: true,
      showBottomButton: true,
      bottomButtonConfig: {
        exceedMaxResultButton: {
          id: 'view-all',
          label: this.translate.instant('VIEW_MORE'),
        },
        lessThanResultButton: {
          id: 'show-other',
          label: this.translate.instant('SEARCH_OTHER_WITH_TEXT', { text: this.translate.instant('CUSTOMER') }),
        },
      },
      data: meetingContactData,
    });

    return sections;
  }

  /** Fires when the rep taps the (read-only) Customer field to open the
   *  picker — the same "engaged this section" moment toggleAddressDropdown /
   *  toggleEmailDropdown mark for their own fields. */
  onCustomerFieldClicked(): void {
    if (this.touched.contact) return;
    this.touched.contact = true;
    this._buildContactFormField();
    this.cd.markForCheck();
  }

  onContactFormAdditionalControl(ev: any): void {
    const id = typeof ev === 'string' ? ev : ev?.id;
    if (id === 'expandblue' || id === 'view-all' || id === 'show-other') {
      this.openContactList();
    }
  }

  private _contactDisplayName(contact: Contact): string {
    return contact.fullName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
  }

  private _lookupContact(id: string): Contact | undefined {
    return this.contactService.getContactByID(id) || this.contactService.contacts.find((c) => c.ID === id);
  }

  /** Clears the chosen customer and everything it drove (address/email options). */
  clearCustomer(): void {
    if (this.isLocked || !this.formState) return;
    this.formState.contactId = '';
    this.formState.contactName = '';
    this.formState.deliveryAddressId = '';
    this.formState.deliveryAddressLabel = '';
    this.formState.emailId = '';
    this.formState.emailLabel = '';
    this.addressOptions = [];
    this.emailOptions = [];
    this.isAddressDropdownOpen = false;
    this.isEmailDropdownOpen = false;
    this.selectedContact = null;
    this._buildContactFormField();
    this.cd.markForCheck();
  }

  // ──────────────── Address / Email dropdowns ────────────────

  /** Memo for _resolveSavedAddressLabel, keyed `contactId|addressId`. Cleared
   *  when the linked-entity worker lands, since an unresolved lookup then
   *  becomes resolvable. */
  private _addressLabelCache: { key: string; label: string } | null = null;

  /**
   * Full composite text for a SAVED (looked-up) delivery address.
   *
   * `deliveryAddressName` is the Dynamics lookup's FormattedValue — the address
   * RECORD'S NAME ("Mathikere"), not the composite line the rep actually picked
   * ("Mathikere, 7th main 4th cross, Bangalore, Karnataka, India"). Only the
   * address id is ever persisted, so the composite has to be re-resolved from
   * the contact's own address list on each hydration. Without this, a freshly
   * picked address showed the full composite (applyContact reads
   * `primaryAddress.compositeAdd`) while the same record after any remount
   * showed the truncated name — and `_syncSdrContactCache` then wrote that
   * truncated value back onto `this.sdr`, so every later mount re-truncated it
   * (OMNI-62537).
   */
  private _resolveSavedAddressLabel(contactId: string, addressId: string): string {
    if (!addressId) return '';
    const key = `${contactId ?? ''}|${addressId}`;
    if (this._addressLabelCache?.key === key) return this._addressLabelCache.label;
    // getContactByID is a linear scan over the whole contact array, and this
    // runs from a template getter — resolve once per contact/address pair.
    const contact = this.selectedContact?.ID === contactId
      ? this.selectedContact
      : this.contactService.getContactByID(contactId);
    const label = (contact?.addressesList ?? []).find((a) => a.addressId === addressId)?.compositeAdd ?? '';
    this._addressLabelCache = { key, label };
    return label;
  }

  private _rebuildOptionsFromContact(): void {
    this.addressOptions = (this.selectedContact?.addressesList ?? []).map((a) => ({
      id: a.addressId,
      label: a.compositeAdd ?? '',
      isPrimary: !!a.isPrimary,
    }));
    this.emailOptions = (this.selectedContact?.emailAddressList ?? [])
      .filter((e) => !e.statecode || e.statecode !== 1)
      .map((e) => ({
        id: e.emailAddressId,
        label: e.emailAddress ?? '',
        isPrimary: !!e.isPrimary,
      }));
    if (this.formState) {
      // Same rationale as _initResumeState's fresh-draft path: only default
      // into free-text mode when a contact IS selected but has nothing saved
      // — never when there's no contact at all, which would otherwise let
      // the rep type an address/email before ever picking a customer.
      this.formState.useAlternateAddress = !!this.selectedContact && this.addressOptions.length === 0;
      if (!this.formState.useAlternateAddress) this.formState.alternateDeliveryAddress = '';
      this.formState.useAlternateEmail = !!this.selectedContact && this.emailOptions.length === 0;
      if (!this.formState.useAlternateEmail) this.formState.alternateEmail = '';
    }
  }

  /**
   * Handler for FINISHEDSERVICEWORKERMAPPING (see constructor). Backfills
   * deliveryAddress/email on this Draft if they're still unset because the
   * worker's linked-entity merge hadn't reached this contact yet when the
   * form first initialized. Deliberately narrow: only fills a field that is
   * BOTH not in alternate/free-text mode AND still empty — a rep's manual
   * alternate address/email, or a saved (non-primary) address they already
   * picked, must never be overwritten by this. Does not call
   * _rebuildOptionsFromContact, since that also resets
   * useAlternateAddress/useAlternateEmail whenever options are non-empty,
   * which would clobber a deliberate alternate choice made in the meantime.
   */
  private _reapplyContactDetailsIfMissing(): void {
    if (this.isLocked || !this.formState || !this.selectedContact) return;

    let changed = false;
    if (!this.formState.useAlternateAddress && !this.formState.deliveryAddressId) {
      const primary = this.selectedContact.primaryAddress;
      if (primary) {
        this.formState.deliveryAddressId = primary.addressId ?? '';
        this.formState.deliveryAddressLabel = primary.compositeAdd ?? '';
        changed = true;
      }
    }
    if (!this.formState.useAlternateEmail && !this.formState.emailId) {
      const primaryEmail = this.selectedContact.emailAddressList?.find((e) => e.isPrimary);
      if (primaryEmail) {
        this.formState.emailId = primaryEmail.emailAddressId ?? '';
        this.formState.emailLabel = primaryEmail.emailAddress ?? '';
        changed = true;
      }
    }
    if (!changed) return;

    // Keep the dropdown's pickable list in sync with what was just backfilled
    // — a plain array rebuild only, no flag side effects (see doc comment above).
    this.addressOptions = (this.selectedContact.addressesList ?? []).map((a) => ({
      id: a.addressId,
      label: a.compositeAdd ?? '',
      isPrimary: !!a.isPrimary,
    }));
    this.emailOptions = (this.selectedContact.emailAddressList ?? [])
      .filter((e) => !e.statecode || e.statecode !== 1)
      .map((e) => ({
        id: e.emailAddressId,
        label: e.emailAddress ?? '',
        isPrimary: !!e.isPrimary,
      }));

    this._buildContactFormField();
    this.cd.markForCheck();
    this._persistContactDetails();
  }

  get isDeliveryAddressPrimary(): boolean {
    return !!this.addressOptions.find((a) => a.id === this.formState?.deliveryAddressId)?.isPrimary;
  }

  get isEmailPrimary(): boolean {
    return !!this.emailOptions.find((e) => e.id === this.formState?.emailId)?.isPrimary;
  }

  toggleAddressDropdown(): void {
    if (this.isLocked) return;
    this.isAddressDropdownOpen = !this.isAddressDropdownOpen;
    this.isEmailDropdownOpen = false;
    this.closePicker();
    this.touched.deliveryAddress = true;
  }

  toggleEmailDropdown(): void {
    if (this.isLocked) return;
    this.isEmailDropdownOpen = !this.isEmailDropdownOpen;
    this.isAddressDropdownOpen = false;
    this.closePicker();
    this.touched.email = true;
  }

  selectAddress(addr: SdrAddressOption): void {
    this.formState.deliveryAddressId = addr.id;
    this.formState.deliveryAddressLabel = addr.label;
    this.isAddressDropdownOpen = false;
    this._persistContactDetails();
  }

  selectEmail(opt: SdrEmailOption): void {
    this.formState.emailId = opt.id;
    this.formState.emailLabel = opt.label;
    this.isEmailDropdownOpen = false;
    this._persistContactDetails();
  }

  trackByOptionId(_: number, item: { id: string }): string {
    return item.id;
  }

  // ──────────────── Alternate (free-text) address / email ────────────────

  get isAlternateAddressActive(): boolean {
    return !!this.formState?.useAlternateAddress;
  }
  get isAlternateEmailActive(): boolean {
    return !!this.formState?.useAlternateEmail;
  }
  get hasSavedAddresses(): boolean {
    return this.addressOptions.length > 0;
  }
  get hasSavedEmails(): boolean {
    return this.emailOptions.length > 0;
  }

  get isDeliveryAddressInvalid(): boolean {
    return this.touched.deliveryAddress && !this.sdrService.getFieldValidity(this.formState).deliveryAddress;
  }
  get isEmailInvalid(): boolean {
    return this.touched.email && !this.sdrService.getFieldValidity(this.formState).email;
  }

  /** OMNI-60495: hide the mandatory-field icon when the BU has turned off the email requirement. */
  get isEmailMandatory(): boolean {
    return this.sdrService.isEmailMandatory;
  }

  get isProductsInvalid(): boolean {
    return this.touched.products && !this.sdrService.getFieldValidity(this.formState).products;
  }

  enableAlternateAddress(): void {
    this.formState.useAlternateAddress = true;
    this.formState.deliveryAddressId = '';
    this.formState.deliveryAddressLabel = '';
    this.isAddressDropdownOpen = false;
    this.touched.deliveryAddress = false;
  }

  useSavedAddress(): void {
    this.formState.useAlternateAddress = false;
    this.formState.alternateDeliveryAddress = '';
    this.touched.deliveryAddress = false;
    if (this.addressOptions.length === 1) {
      this.selectAddress(this.addressOptions[0]);
    } else {
      this.isAddressDropdownOpen = true;
    }
  }

  onAlternateAddressInput(ev: Event): void {
    this.formState.alternateDeliveryAddress = (ev.target as HTMLInputElement).value ?? '';
    this.touched.deliveryAddress = true;
  }

  onAlternateAddressBlur(): void {
    this.touched.deliveryAddress = true;
    this._persistContactDetails();
  }

  enableAlternateEmail(): void {
    this.formState.useAlternateEmail = true;
    this.formState.emailId = '';
    this.formState.emailLabel = '';
    this.isEmailDropdownOpen = false;
    this.touched.email = false;
  }

  useSavedEmail(): void {
    this.formState.useAlternateEmail = false;
    this.formState.alternateEmail = '';
    this.touched.email = false;
    if (this.emailOptions.length === 1) {
      this.selectEmail(this.emailOptions[0]);
    } else {
      this.isEmailDropdownOpen = true;
    }
  }

  onAlternateEmailInput(ev: Event): void {
    this.formState.alternateEmail = (ev.target as HTMLInputElement).value ?? '';
    this.touched.email = true;
  }

  onAlternateEmailBlur(): void {
    this.touched.email = true;
    this._persistContactDetails();
  }

  get isAlternateEmailInvalid(): boolean {
    const v = this.formState?.alternateEmail?.trim();
    return this.isAlternateEmailActive && this.touched.email && !!v && !isValidEmail(v);
  }

  // ──────────────── Product picker ────────────────

  get filteredPickerSkus(): SdrPickerSku[] {
    // OMNI-61531: only SKUs allocated to the current contact populate the
    // picker — this.sdrSkus (the raw device-wide catalog) is no longer used
    // directly here.
    const eligibleSkus = this.sdrService.getEligibleSkusForContact(this.formState?.contactId, this._allocationAsOf, this._meetingWindow);
    const search = this.pickerSearchText.toLowerCase();
    return search
      ? eligibleSkus.filter((s) => s.skuName?.toLowerCase().includes(search))
      : eligibleSkus;
  }

  openPicker(): void {
    if (this.isLocked) return;
    this.isPickerOpen = true;
    this.isAddressDropdownOpen = false;
    this.isEmailDropdownOpen = false;
    this.touched.products = true;
  }

  closePicker(): void {
    this.isPickerOpen = false;
    this.pickerSearchText = '';
  }

  onPickerInput(): void {
    this.openPicker();
  }

  onPickerRowClick(sku: SdrPickerSku): void {
    if (this.isSkuInForm(sku.skuId)) return;
    this.addSkuToForm(sku);
  }

  isSkuInForm(skuId: string): boolean {
    return !!this.formState?.products?.some((p) => p.skuId === skuId);
  }

  hasSkuLimit(skuId: string): boolean {
    return this.limitsMap.has(skuId?.toLowerCase());
  }

  getSkuLimitLabel(skuId: string): string {
    const entry = this.limitsMap.get(skuId?.toLowerCase());
    if (!entry) return '';
    const hasMin = entry.min != null;
    const hasMax = entry.max != null;
    if (hasMin && hasMax) return this.translate.instant('SDR_PRODUCT_LIMIT', { min: entry.min, max: entry.max });
    if (hasMin) return this.translate.instant('SDR_PRODUCT_LIMIT_MIN', { min: entry.min });
    if (hasMax) return this.translate.instant('SDR_PRODUCT_LIMIT_MAX', { max: entry.max });
    return '';
  }

  getSkuMinQty(skuId: string): number {
    return this.limitsMap.get(skuId?.toLowerCase())?.min ?? 1;
  }

  getSkuMaxQty(skuId: string): number | null {
    return this.limitsMap.get(skuId?.toLowerCase())?.max ?? null;
  }

  async addSkuToForm(sku: SdrPickerSku): Promise<void> {
    if (this.isSkuInForm(sku.skuId)) return;
    // Online-only, TTL-guarded refresh of this contact's allocation config
    // right before checking it — mirrors NewSampleDeliveryRequestComponent.
    // addSkuToForm. syncManualAllocationsForContact itself no-ops offline
    // (falls back to the last disk snapshot) and skips the network call if
    // this contact was already fetched within the last 3 minutes, so this is
    // cheap for successive adds and never blocks offline.
    await this.sdrService.syncManualAllocationsForContact(this.formState.contactId);
    const minQty = this.getSkuMinQty(sku.skuId);
    const maxQty = this.getSkuMaxQty(sku.skuId);
    // Best-effort optimistic pre-check (OMNI-59706), mirrors
    // NewSampleDeliveryRequestComponent.addSkuToForm.
    const display = this._resolveAllocationDisplay(this.formState.contactId, sku.skuId);
    const qty = Math.max(minQty, 1);
    this.formState.products.push({
      skuId: sku.skuId,
      skuName: sku.skuName,
      productId: sku.brandId,
      productName: sku.brandName,
      qty,
      minQty,
      maxQty,
      hasLimit: this.hasSkuLimit(sku.skuId),
      allocationRemaining: display.remaining,
      allocationUnlimited: display.unlimited,
      allocationNotEligible: display.notEligible,
      allocationEligible: display.eligible,
      allocationStart: display.start,
      allocationEnd: display.end,
    });
    const newProduct = this.formState.products[this.formState.products.length - 1];
    this._enqueuePersist(async () => {
      if (!this.draftSdrId) return;
      if (!this.formState.products.includes(newProduct)) return; // this exact instance was removed before this op ran
      // isAllocationBlocking (behind allocationNotEligible, computed above) is
      // a CONFIRMED signal, not a guess — its own doc comment records it was
      // verified live against Exeevo.AllocationSR.PreOperationSRLineItem,
      // which unconditionally rejects a SKU with zero matching allocation
      // configs. Unlike the standalone atomic-submit form (which still
      // attempts every line and lets Dynamics reject each independently at
      // submit time — a deliberate OMNI-59706 choice), THIS component fires
      // the create call the instant a product is added, so an already-
      // confirmed-doomed call is pure wasted latency plus a confusing plugin
      // error round-trip. Skip it and surface the same failure toast the rep
      // would have gotten anyway, just immediately instead of after a
      // round-trip.
      if (newProduct.allocationNotEligible) {
        this.notificationService.notify(
          this.translate.instant('SDR_PRODUCT_SAVE_FAILED', { product: newProduct.skuName }),
          'inline-sample-delivery-request',
          'top',
          ToastStyle.DANGER,
        );
        await this._syncSdrProductsCache();
        return;
      }
      if (this.device.isOffline) {
        await this.dataService.addSdrLineOffline(this.draftSdrId, newProduct);
      } else {
        const result = await this.dataService.addSdrLine(this.draftSdrId, newProduct);
        if (result.status === 'accepted') {
          this.productLineIds.set(newProduct, result.lineId);
          // The add already carried this qty — record it so an immediate
          // tap-up-then-back-down doesn't re-send an identical value.
          this.persistedQtys.set(newProduct, newProduct.qty);
        } else {
          // Confirmed rejected server-side even though our own pre-check
          // passed — e.g. another request for the same contact+SKU was
          // submitted and decremented the balance in the meantime.
          this.notificationService.notify(
            this.translate.instant('SDR_PRODUCT_SAVE_FAILED', { product: newProduct.skuName }),
            'inline-sample-delivery-request',
            'top',
            ToastStyle.DANGER,
          );
        }
      }
      // Keep the app-wide allSdrs cache (and thus a fresh mount's [sdr] input)
      // in sync with formState the instant this line is durably persisted —
      // see _syncSdrProductsCache's own doc comment for why this can't wait
      // for final Submit.
      await this._syncSdrProductsCache();
    });
    this.closePicker();
  }

  /** True when a Manual-Distribution Customer Allocation record was found for
   *  this SKU (i.e. there's a remaining balance worth showing as a chip) —
   *  mirrors NewSampleDeliveryRequestComponent.hasAllocationInfo. */
  hasAllocationInfo(product: SdrProductLine): boolean {
    return !product.allocationUnlimited && product.allocationRemaining != null;
  }

  getAllocationLabel(product: SdrProductLine): string {
    if (!this.hasAllocationInfo(product)) return '';
    return this.translate.instant('SDR_ALLOCATION_REMAINING', { remaining: product.allocationRemaining });
  }

  hasAllocationDetails(product: SdrProductLine): boolean {
    return this.sdrService.hasAllocationDetails(product);
  }

  getAllocationValidity(product: SdrProductLine): string {
    return this.sdrService.formatAllocationValidity(product);
  }

  removeProduct(index: number): void {
    const product = this.formState.products[index];
    this.formState.products.splice(index, 1);
    this.touched.products = true;
    if (!product) return;
    this._cancelPendingQtyPersist(product);
    this._enqueuePersist(async () => {
      if (!this.draftSdrId) return;
      const lineId = this.productLineIds.get(product);
      this.productLineIds.delete(product);
      this.persistedQtys.delete(product);
      const branch = lineId ? (this.device.isOffline ? 'queueSdrLineDeleteOffline' : 'removeSdrLine') : 'removeSdrLineOffline';
      console.log(`[SDR-REMOVE] removeProduct: sdrId=${this.draftSdrId} skuId=${product.skuId} lineId=${lineId ?? '(none)'} isOffline=${this.device.isOffline} branch=${branch}`);
      if (lineId && this.device.isOffline) {
        // A real server-side line exists, but there's no network to delete
        // it right now — queue it for uploadPendingSdrs to delete on
        // reconnect instead of attempting (and losing) a doomed live call.
        await this.dataService.queueSdrLineDeleteOffline(this.draftSdrId, lineId);
      } else if (lineId) {
        // A real server-side line exists (the add resolved successfully
        // online before this ran) — delete it there.
        try {
          await this.dataService.removeSdrLine(lineId);
          console.log(`[SDR-REMOVE] removeProduct: removeSdrLine succeeded for lineId=${lineId}`);
        } catch (err) {
          console.error(`[SDR-REMOVE] removeProduct: removeSdrLine FAILED for lineId=${lineId} — line NOT deleted from Dynamics`, err);
          throw err;
        }
      } else if (product.skuId) {
        // Either offline-added (no server id ever existed), or the add is
        // still in-flight/was rejected when this runs — try the local
        // pending-doc removal defensively; it's a harmless no-op if the SKU
        // was never staged there (e.g. the add succeeded online, in which
        // case lineId above would have been set and this branch wouldn't
        // run at all).
        await this.dataService.removeSdrLineOffline(this.draftSdrId, product.skuId);
      }
      // Keep the allSdrs cache in sync — see _syncSdrProductsCache's doc
      // comment. Skipped on a thrown removeSdrLine failure above (the line
      // still real on the server), matching the enclosing catch's own intent.
      await this._syncSdrProductsCache();
    });
  }

  getQtyErrorKey(product: SdrProductLine): string | null {
    switch (this.sdrService.getProductQtyError(product)) {
      case 'notEligible': return 'SDR_CUSTOMER_NOT_ELIGIBLE';
      case 'range': return 'SDR_QTY_OUTSIDE_RANGE';
      case 'zero': return 'SDR_QTY_SET_HINT';
      case 'allocation': return 'SDR_QTY_EXCEEDS_ALLOCATION';
      default: return null;
    }
  }

  isDecrementDisabled(product: SdrProductLine): boolean {
    const floor = product.hasLimit ? product.minQty : 0;
    return product.qty <= floor;
  }

  isIncrementDisabled(product: SdrProductLine): boolean {
    return product.maxQty !== null && product.qty >= product.maxQty;
  }

  updateQty(index: number, delta: number): void {
    const product = this.formState.products[index];
    if (delta < 0 && this.isDecrementDisabled(product)) return;
    if (delta > 0 && this.isIncrementDisabled(product)) return;
    product.qty = product.qty + delta;
    this._persistProductQty(product);
  }

  onGridQtyInput(index: number, ev: Event): void {
    const product = this.formState.products[index];
    const raw = Number((ev.target as HTMLInputElement).value);
    if (Number.isNaN(raw)) return;
    product.qty = Math.max(0, Math.floor(raw));
    this._persistProductQty(product);
  }

  /** Save-on-click for a line's quantity. Enqueued on the same FIFO queue as
   *  the line's own addSdrLine, so by the time this runs that add has settled
   *  and productLineIds reflects its real outcome. Reads product.qty fresh
   *  inside the op (not captured), so rapid stepper taps collapse to the
   *  final value rather than replaying every intermediate one.
   *  Branches on lineId PRESENCE, not live isOffline — same rationale as
   *  removeProduct and the pre-finalize resend in submit(): a line added
   *  online must patch online even if the device is now offline, and an
   *  offline-added line has no server row to patch. A line whose add was
   *  rejected has neither, so both branches no-op.
   *  Only a VALID quantity is sent — Sample Drop's equivalent
   *  (new-sample-activity's _updateCurrentSampleActivity) is likewise called
   *  only from the post-validation `else` branch, never for a qty that failed
   *  its limit/range check. An invalid value stays local, shows its inline
   *  error, and submit() is already blocked on it by validateForSubmit. */
  private _persistProductQty(product: SdrProductLine): void {
    const pending = this.qtyPersistTimers.get(product);
    if (pending) clearTimeout(pending);
    this.qtyPersistTimers.set(
      product,
      setTimeout(() => {
        this.qtyPersistTimers.delete(product);
        this._enqueueProductQtyPersist(product);
      }, QTY_PERSIST_DEBOUNCE_MS),
    );
  }

  /** Cancel a line's pending qty save (removal, destroy, finalize). */
  private _cancelPendingQtyPersist(product: SdrProductLine): void {
    const pending = this.qtyPersistTimers.get(product);
    if (pending) {
      clearTimeout(pending);
      this.qtyPersistTimers.delete(product);
    }
  }

  private _enqueueProductQtyPersist(product: SdrProductLine): void {
    this._enqueuePersist(async () => {
      if (!this.draftSdrId) return;
      if (!this.formState.products.includes(product)) return; // removed before this op ran
      if (this.sdrService.getProductQtyError(product) !== null) return;
      if (this.persistedQtys.get(product) === product.qty) return; // unchanged since last send
      const qty = product.qty;
      try {
        const lineId = this.productLineIds.get(product);
        if (lineId) {
          await this.dataService.updateSdrLineQty(lineId, qty);
        } else if (product.skuId) {
          await this.dataService.updateSdrLineQtyOffline(this.draftSdrId, product.skuId, qty);
        } else {
          return;
        }
        this.persistedQtys.set(product, qty);
        // Keep the allSdrs cache in sync — see _syncSdrProductsCache's doc
        // comment. Skipped on a caught failure below (the confirmed
        // server-side qty is what persistedQtys/the reverted display now show).
        await this._syncSdrProductsCache();
      } catch (err) {
        console.error('InlineSampleDeliveryRequestComponent: qty save-on-click failed', product.skuId, err);
        // The PATCH never landed — the server-side line still holds the last
        // CONFIRMED qty. Revert the displayed qty to it so the form doesn't
        // claim a value the server rejected.
        const lastConfirmed = this.persistedQtys.get(product) ?? product.qty;
        if (lastConfirmed !== product.qty) {
          product.qty = lastConfirmed;
          this.cd.markForCheck();
        }
      }
    });
  }

  trackById(_: number, item: SdrPickerSku): string {
    return item.skuId;
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ──────────────── Qty numeric dropdown (mirrors IAO openQuantityDropdown) ────────────────

  openQtyDropdown(event: Event, productIndex: number): void {
    event.stopPropagation();
    if (this._qtyDropdownEl) {
      this._destroyQtyDropdown();
      return;
    }
    const product = this.formState.products[productIndex];
    if (!product) return;

    const btn = (event.target as HTMLElement).closest('.sdr-qty-dropdown-btn') as HTMLElement;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const min = product.hasLimit ? Math.max(1, product.minQty) : 1;
    const max = product.maxQty !== null
      ? product.maxQty
      : product.hasLimit
        ? product.minQty + QTY_DROPDOWN_DEFAULT_MAX - 1
        : QTY_DROPDOWN_DEFAULT_MAX;
    if (max < min) return;

    this._setParentScroll(false);
    this._qtyDropdownEl = this.renderer.createElement('div');
    this.renderer.appendChild(document.body, this._qtyDropdownEl);

    const s = this._qtyDropdownEl.style;
    s.position = 'fixed';
    s.width = '56px';
    s.left = `${rect.left - 18}px`;

    const dropdownMaxH = 240;
    const spaceBelow = window.innerHeight - rect.bottom - 4;
    if (spaceBelow < dropdownMaxH && rect.top > spaceBelow) {
      s.bottom = `${window.innerHeight - rect.top + 4}px`;
    } else {
      s.top = `${rect.bottom + 4}px`;
    }
    s.zIndex = '99999';
    s.background = '#ffffff';
    s.border = '1px solid #e5e5ea';
    s.borderRadius = '4px';
    s.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
    s.maxHeight = `${dropdownMaxH}px`;
    s.overflowY = 'auto';
    (s as any)['-webkit-overflow-scrolling'] = 'touch';

    let html = '';
    for (let i = min; i <= max; i++) {
      const isActive = i === product.qty;
      html += `<div class="sdr-qty-dd-option" data-value="${i}" style="padding:6px 0;text-align:center;font-size:14px;font-weight:${isActive ? '600' : '400'};color:${isActive ? '#007aff' : '#1a1a1a'};background:${isActive ? 'rgba(0,122,255,0.08)' : 'transparent'};cursor:pointer;">${i}</div>`;
    }
    this._qtyDropdownEl.innerHTML = html;

    const activeOpt = this._qtyDropdownEl.querySelector(`[data-value="${product.qty}"]`) as HTMLElement;
    if (activeOpt) activeOpt.scrollIntoView({ block: 'center' });

    const options = this._qtyDropdownEl.querySelectorAll('.sdr-qty-dd-option');
    options.forEach((el: HTMLElement) => {
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const val = parseInt(el.getAttribute('data-value') ?? '', 10);
        if (!Number.isNaN(val)) {
          this.ngZone.run(() => {
            product.qty = val;
            this._persistProductQty(product);
            this._destroyQtyDropdown();
            this.cd.markForCheck();
          });
        }
      });
      el.addEventListener('mouseover', () => {
        if (parseInt(el.getAttribute('data-value') ?? '', 10) !== product.qty) {
          el.style.background = '#f2f2f7';
        }
      });
      el.addEventListener('mouseout', () => {
        if (parseInt(el.getAttribute('data-value') ?? '', 10) !== product.qty) {
          el.style.background = 'transparent';
        }
      });
    });

    const outsideHandler = (e: MouseEvent) => {
      if (this._qtyDropdownEl && !this._qtyDropdownEl.contains(e.target as Node)) {
        this._destroyQtyDropdown();
      }
    };
    setTimeout(() => document.addEventListener('mousedown', outsideHandler));
    this._qtyDropdownCleanup = () => document.removeEventListener('mousedown', outsideHandler);
  }

  private _destroyQtyDropdown(): void {
    if (this._qtyDropdownEl) {
      this._qtyDropdownEl.remove();
      this._qtyDropdownEl = null;
    }
    if (this._qtyDropdownCleanup) {
      this._qtyDropdownCleanup();
      this._qtyDropdownCleanup = null;
    }
    this._setParentScroll(true);
  }

  private _setParentScroll(enabled: boolean): void {
    if (!this._parentIonContent) {
      this._parentIonContent = this.elRef.nativeElement.closest('ion-content') as HTMLIonContentElement;
    }
    if (this._parentIonContent) {
      this._parentIonContent.scrollY = enabled;
    }
  }

  // ──────────────── Submit ────────────────

  private get notesGate(): SdrNotesGate {
    const section = this.notesSection;
    if (!section) {
      return { notesMandatory: false, notesSatisfied: true, attachmentsMandatory: false, attachmentsSatisfied: true };
    }
    return {
      notesMandatory: section.notesMandatory,
      notesSatisfied: section.hasText,
      attachmentsMandatory: section.attachmentsMandatory,
      attachmentsSatisfied: section.hasAttachment,
    };
  }

  get isSubmitEnabled(): boolean {
    // Note: no longer checks sdrService.isSubmittingId — that flag is only
    // ever set by sdrService.submit()/submitOffline(), which this component's
    // submit() no longer calls (see submit()'s own doc comment), so it can
    // never become true for this draft. this.isSubmitting alone is the
    // correct in-flight guard for this component's own finalize call.
    return !this.isLocked
      && this.sdrService.isFormValid(this.formState, this.notesGate)
      && !this.isSubmitting;
  }

  /**
   * Finalizes the already-saved Draft: every product line, note, and
   * contact/address/email change reached Dynamics (or the local offline
   * store) individually via save-on-click as the rep edited the form (see
   * addSkuToForm/removeProduct, onStagedNoteAdded/Removed, applyContact).
   * There is nothing left to CREATE here — this only (1) waits for any
   * still-in-flight save-on-click calls to finish, (2) best-effort re-sends
   * the current contact/address/email once more if online (closing most of
   * the gap noted in applyContact's save-on-click comment — no offline
   * sibling exists for updateSdrDraftContact), then (3) flips the Draft's
   * statuscode from Active to Submitted via the idempotent finalizeSdr.
   */
  async submit(): Promise<void> {
    if (this.isSubmitting) return;

    const reason = this.sdrService.validateForSubmit(this.formState, this.notesGate);
    if (reason === 'requiredFields') { this._toast('SDR_VALIDATION_REQUIRED_FIELDS', ToastStyle.DANGER); return; }
    if (reason === 'invalidEmail') { this._toast('SDR_INVALID_EMAIL', ToastStyle.DANGER); return; }
    if (reason === 'notes') { this._toast(this.notesSection?.notesRequiredMessageKey ?? 'SDR_NOTE_REQUIRED', ToastStyle.DANGER); return; }
    if (reason === 'productQty') { this._toast('SDR_VALIDATION_PRODUCT_QTY', ToastStyle.DANGER); return; }

    // Set BEFORE the possible await below (not after it) — a double-tap on
    // Submit would otherwise sail past the isSubmitting guard above on BOTH
    // calls (it's only checked once, synchronously, at the top) and race
    // through the rest of this method concurrently (duplicate PATCHes,
    // duplicate toasts, two onSaved.emit() calls).
    this.isSubmitting = true;
    this.cd.detectChanges(); // render the submitting state before the await (OnPush)

    // Drop any debounced qty save still waiting out its quiet period — it
    // would fire mid-finalize (or after) and PATCH a line on an already-
    // Submitted record. Nothing is lost: the pre-finalize resend loop below
    // re-sends every product's current qty anyway.
    this.qtyPersistTimers.forEach((timer) => clearTimeout(timer));
    this.qtyPersistTimers.clear();

    // Flush whatever's still sitting in the notes composer (text and/or a
    // staged attachment the rep never tapped Send on) so it isn't silently
    // dropped by a direct tap on Submit. saveNewNote() no-ops safely if
    // there's nothing to save; when there is, it stages it AND emits
    // stagedNoteAdded, which onStagedNoteAdded enqueues onto the same
    // _persistQueue awaited right below — so it's guaranteed to land before
    // finalize.
    this.notesSection?.saveNewNote();

    if (!this.draftSdrId) {
      // Should be unreachable: the parent (createNewInlineSdr) creates the
      // header record before this component ever mounts, so _initResumeState
      // always sets draftSdrId synchronously in ngOnInit. Kept as a defensive
      // fallback rather than removed outright.
      await this._draftReady;
    }
    if (!this.draftSdrId) {
      console.error('Inline SDR submit: no draftSdrId after awaiting _draftReady');
      this._toast('ERROR_SDR_SUBMIT_FAILED', ToastStyle.DANGER);
      this.isSubmitting = false;
      this.cd.markForCheck();
      return;
    }

    try {
      // Every save-on-click call already queued (product/note/contact edits)
      // must land before finalizing — otherwise a line added in the same tick
      // as Submit could finalize the header to Submitted before its own
      // addSdrLine has actually committed.
      await this._persistQueue;

      // Catch-up sweep for any qty NOT already persisted by its own autosave.
      // Qty now autosaves on change (debounced, see _persistProductQty), so
      // this is no longer the only checkpoint it once was — but it is still
      // required, for exactly two cases:
      //   (a) a change still inside its debounce window when Submit was
      //       tapped — the timers are cancelled above, so this sweep is what
      //       actually saves it;
      //   (b) an autosave PATCH that failed (it leaves persistedQtys
      //       un-updated precisely so this retries it).
      // persistedQtys records the last value successfully sent, so a line
      // already in sync costs ZERO calls here — in the common case (rep
      // adjusted qty, paused, then submitted) the whole loop is a no-op
      // rather than the one-PATCH-per-line it used to be unconditionally.
      // Best-effort throughout: logged not surfaced, never blocks finalize.
      // Branches on lineId PRESENCE (where the record actually lives), not
      // live this.device.isOffline — same rationale as removeProduct's own
      // branch above. Live connectivity at submit time can differ from the
      // connectivity the line was ADDED under: a line added ONLINE (real
      // lineId) with the device now offline must still patch online (it was
      // never staged in pendingFormPayload.products, so the offline sibling
      // would silently find nothing); a line added OFFLINE (no lineId) with
      // the device now back online must still patch the LOCAL pending doc
      // (there's no server-side row yet to patch online). productLineIds
      // reflects the FINAL outcome of every add/remove op (already settled
      // by the _persistQueue await above); a line whose add was REJECTED (no
      // record exists anywhere) has no lineId AND no matching offline entry,
      // so both branches silently no-op for it — consistent with there being
      // nothing to update for a line that was never actually created.
      for (const product of this.formState.products) {
        if (this.persistedQtys.get(product) === product.qty) continue; // already in sync
        try {
          const qty = product.qty;
          const lineId = this.productLineIds.get(product);
          if (lineId) {
            await this.dataService.updateSdrLineQty(lineId, qty);
          } else if (product.skuId) {
            await this.dataService.updateSdrLineQtyOffline(this.draftSdrId, product.skuId, qty);
          } else {
            continue;
          }
          this.persistedQtys.set(product, qty);
        } catch (err) {
          console.error('Inline SDR submit: pre-finalize qty resend failed for', product.skuId, err);
        }
      }

      // Best-effort re-patch of the current contact/address/email, online
      // only, right before finalizing — a fast-follow closing (most of) the
      // gap noted on applyContact's save-on-click hook: an earlier failed
      // online patch, or a contact changed while offline where the rep is
      // now back online by the time they finalize. Does not (and cannot)
      // help if the device is STILL offline at finalize time — there's no
      // offline sibling for updateSdrDraftContact, by design (see
      // applyContact's own doc comment for the accepted limitation there).
      if (!this.device.isOffline) {
        try {
          await this.dataService.updateSdrDraftContact(this.draftSdrId, {
            contactId: this.formState.contactId,
            deliveryAddress: this.formState.useAlternateAddress ? undefined : this.formState.deliveryAddressId,
            email: this.formState.useAlternateEmail ? undefined : this.formState.emailId,
            alternateAddress: this.formState.useAlternateAddress ? this.formState.alternateDeliveryAddress : undefined,
            alternateEmail: this.formState.useAlternateEmail ? this.formState.alternateEmail : undefined,
          });
        } catch (err) {
          // Best-effort only — do not block finalize on this. If the contact
          // was already correctly patched by applyContact's own save-on-click
          // call, this is a harmless redundant PATCH; if it fails here too,
          // finalize still proceeds (matching this method's overall "finalize
          // is authoritative for statuscode, not a full re-verification of
          // every field" scope) and the divergence stays whatever it already
          // was — logged, not surfaced twice to the rep.
          console.error('Inline SDR submit: pre-finalize contact re-patch failed', err);
        }
      }

      // Fresh re-check right at the point of no return — this component
      // never calls sdrService.submit()/submitOffline() (it talks to
      // dataService directly, since every line already persisted
      // individually via save-on-click), so nothing else provides this gate.
      const preflight = await this.sdrService.revalidateAllocationsBeforeSubmit(this.formState.contactId, this.formState.products, this._allocationAsOf, this._meetingWindow);
      if (!preflight.ok) {
        const products = preflight.blockedProducts.map((p) => p.skuName).join(', ');
        this.notificationService.notify(
          this.translate.instant('SDR_ALLOCATION_EXCEEDED_AT_SUBMIT', { products }),
          'inline-sample-delivery-request',
          'top',
          ToastStyle.DANGER,
        );
        for (const p of this.formState.products) {
          this.sdrService.applyAllocationDisplay(p, this._resolveAllocationDisplay(this.formState.contactId, p.skuId));
        }
        this.isSubmitting = false;
        this.cd.markForCheck();
        return;
      }

      await this.dataService.finalizeSdr(this.draftSdrId);

      if (!this.device.isOffline) {
        // The server has now actually decremented the real allocation (same
        // transaction as the Submitted transition) — drop the fetched-cache
        // snapshot so the next read re-fetches instead of reusing the
        // now-stale pre-submit figure. Offline: nothing was consumed
        // server-side yet, and the cache is cleared from the sdrSynced$
        // subscription once uploadPendingSdrs actually replays it.
        this.sdrService.clearManualAllocationsForContact(this.formState.contactId);
      }

      this._toast('SDR_SUBMITTED', ToastStyle.INFO);
      // Build the read-only summary view from the in-memory form state — no
      // follow-up fetch needed, everything already saved via save-on-click.
      const saved = new SampleDeliveryRequest({
        indskr_samplerequestid: this.draftSdrId,
        indskr_name: '',
        statuscode: SdrStatus.Submitted,
        // Built entirely client-side (no server round-trip yet), so without this
        // the Status field renders blank on the immediate post-submit view
        // despite statuscode above being correctly set (OMNI-61543).
        'statuscode@OData.Community.Display.V1.FormattedValue': this.translate.instant('SUBMITTED'),
        _indskr_customer_value: this.formState.contactId,
        '_indskr_customer_value@OData.Community.Display.V1.FormattedValue': this.formState.contactName,
        _indskr_address_value: this.formState.useAlternateAddress ? '' : this.formState.deliveryAddressId,
        '_indskr_address_value@OData.Community.Display.V1.FormattedValue': this.formState.useAlternateAddress ? '' : this.formState.deliveryAddressLabel,
        _indskr_emailaddress_value: this.formState.useAlternateEmail ? '' : this.formState.emailId,
        '_indskr_emailaddress_value@OData.Community.Display.V1.FormattedValue': this.formState.useAlternateEmail ? '' : this.formState.emailLabel,
        indskr_alternativeaddress: this.formState.useAlternateAddress ? this.formState.alternateDeliveryAddress : '',
        indskr_alternativeemailaddress: this.formState.useAlternateEmail ? this.formState.alternateEmail : '',
        // Without this, sdrService.fetchSdrsForMeeting's per-meeting filter
        // (s.appointmentId === apptId) excludes this record entirely — the
        // create-time cache entry (createNewInlineSdr, correctly tagged via
        // createSdrDraftOffline's payload.appointmentId) gets overwritten by
        // this object when cacheCreatedSdr below upserts by id, so omitting
        // it here doesn't just misrender the tab, it makes the whole SDR
        // vanish from the Request tab list after submit while offline.
        _indskr_appointment_value: this.meetingId ?? '',
        createdon: new Date().toISOString(),
      });
      saved.products = [...this.formState.products];
      // stagedNotesView is SdrNotesSectionComponent's own IONote-shaped mirror
      // of `staged`, kept in lockstep by array index — exactly the shape the
      // read-only view's [existingNotes] binding (inline-sample-delivery-
      // request.html:32) needs. Without this, `saved.notes` stayed at the
      // constructor's default [] while notesLoaded:true told the read-only
      // view "already fully hydrated, don't re-fetch" — so notes/attachments
      // silently disappeared from the UI right after submit, even though
      // they were correctly staged and later uploaded to Dynamics on
      // reconnect (the underlying data was never lost, only this display).
      saved.notes = this.notesSection?.stagedNotesView ?? [];
      saved.notesLoaded = true;
      this.sdr = saved;

      // Seed sdrService's own allSdrs cache with this SAME accurate object —
      // this component's create/finalize calls go straight through
      // dataService (create-draft-in-parent pattern), never through
      // sdrService.submit()/submitOffline(), so without this allSdrs never
      // learns the record was finalized. fetchSdrsForMeeting's offline branch
      // reads ONLY from allSdrs, and onSdrSaved's _refreshSdrsInMeeting()
      // fully replaces the tab list with that result — so without this, the
      // tab would keep showing the stale Active snapshot allSdrs was seeded
      // with at create time (createNewInlineSdr), and both isReadOnly and the
      // rendered [sdr] input would stay wrong (still-editable form, contact/
      // products blank) even though finalize succeeded (OMNI-61497 follow-up).
      //
      // Deliberately NOT sourced from dataService.getCachedSdrRecord(): that
      // reads the doc via SampleDeliveryRequest.fromCached, which expects an
      // already-synced INSTANCE shape (camelCase `id`/`statusCode`/`products`,
      // as written by saveSdrRecordToDb after a successful uploadPendingSdrs
      // replay) — an offline-created/edited doc is still in raw-Dynamics
      // shape (`indskr_samplerequestid`/`statuscode`, current data live only
      // in pendingFormPayload), which fromCached cannot parse: it silently
      // produces id:'', statusCode: undefined, products: [], and
      // cacheCreatedSdr's `if (!sdr?.id) return` then no-ops the whole call.
      // `saved` above is already correctly shaped and already carries every
      // field entered — no disk read needed.
      //
      // pendingSync must reflect reality before this call: offline, finalizeSdr
      // already persisted the doc with pendingSync:true + pendingFormPayload
      // intact — cacheCreatedSdr must see that to skip its own disk write, or
      // it would overwrite that doc with this synthetic object (which has no
      // pendingFormPayload) and strip the payload uploadPendingSdrs() needs,
      // reintroducing OMNI-59706. Online, finalizeSdr only PATCHes Dynamics
      // directly (no local doc), so cacheCreatedSdr's own disk write is the
      // only thing that seeds one — required, not just harmless, so a later
      // offline resume of this same SDR isn't stranded with no local copy.
      saved.pendingSync = this.device.isOffline;
      try {
        await this.sdrService.cacheCreatedSdr(saved);
      } catch (err) {
        console.error('Inline SDR submit: failed to seed allSdrs cache after finalize', err);
      }

      this.onSaved.emit(saved);
    } catch (err) {
      console.error('Inline SDR finalize failed', err);
      this._toast('ERROR_SDR_SUBMIT_FAILED', ToastStyle.DANGER);
    } finally {
      this.isSubmitting = false;
      this.cd.markForCheck();
    }
  }

  private _toast(key: string, style: ToastStyle): void {
    this.notificationService.notify(
      this.translate.instant(key),
      'inline-sample-delivery-request',
      'top',
      style,
    );
  }

  // ──────────────── Read-only summary helpers ────────────────

  get readOnlyDeliveryAddress(): string {
    return (
      this.sdr?.alternateAddress ||
      this._resolveSavedAddressLabel(this.sdr?.contactId, this.sdr?.deliveryAddress) ||
      this.sdr?.deliveryAddressName ||
      ''
    );
  }

  get readOnlyEmail(): string {
    return this.sdr?.alternateEmail || this.sdr?.emailAddress || '';
  }
}
